"use client";

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { ResumeData, defaultResumeData, sampleResumeData, resumeToYaml, yamlToResume, createEmptyExperience, createEmptyEducation, createEmptyEntry, ExperienceEntry, EducationEntry, SUPPORTED_NETWORKS, SupportedNetwork, SocialNetwork, DesignSettings, ENTRY_TYPES, EntryTypeId, AnyEntry, createEmptyPublicationEntry, PublicationEntry, generateId, RESUME_STORAGE_KEY, JD_STORAGE_KEY } from '@/stores/resumeStore';
import { useTheme } from '@/contexts/ThemeContext';
import LimitReachedDialog from '@/components/LimitReachedDialog';
import MasterPanel from '@/components/MasterPanel';
import dynamic from 'next/dynamic';

// Dynamic import for Monaco editor (client-only)
const YamlEditor = dynamic(() => import('@/components/YamlEditor'), {
  ssr: false,
  loading: () => <div className="flex items-center justify-center h-full min-h-[400px] bg-transparent animate-pulse">
            <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-slate-400 text-sm">Loading YAML Editor...</span>
            </div>
        </div>
});

// API Base URL
// API Base URL
const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
const WS_BASE = API_BASE.replace(/^http/, 'ws');
import { useAuth } from '@/context/AuthContext';
export default function EditorPage() {
  // Global theme
  const {
    theme: globalTheme
  } = useTheme();
  const {
    user
  } = useAuth();
  const isDarkMode = globalTheme === 'dark';

  // Resume state - use sample data by default so users can see the structure
  const [resume, setResume] = useState<ResumeData>(sampleResumeData);
  const [resumeTheme, setResumeTheme] = useState<string>('classic');
  const [themes, setThemes] = useState<Array<{
    id: string;
    name: string;
    description: string;
  }>>([]);

  // Preview state
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);

  // Scoring state
  const [score, setScore] = useState<any>(null);
  const [scoreLoading, setScoreLoading] = useState(false);

  // Job description for scoring
  const [jobDescription, setJobDescription] = useState('');
  const [showJdInput, setShowJdInput] = useState(false);

  // UI state
  const [activeSection, setActiveSection] = useState<string>('personal');
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // AI Coach state
  const [showCoach, setShowCoach] = useState(false);

  // Undo/Redo state
  const [undoStack, setUndoStack] = useState<ResumeData[]>([]);
  const [redoStack, setRedoStack] = useState<ResumeData[]>([]);

  // Options dropdown state (consolidates Theme, Export, Load Template)
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);

  // Drag-and-drop state
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const [dragOverItemId, setDragOverItemId] = useState<string | null>(null);
  const [coachMessages, setCoachMessages] = useState<Array<{
    role: 'user' | 'assistant';
    content: string;
  }>>([{
    role: 'assistant',
    content: "👋 I'm your AI Resume Coach! Ask me anything about improving your resume:\n\n• How can I make my experience more impactful?\n• What keywords should I add for this role?\n• Can you suggest a better summary?"
  }]);
  const [coachInput, setCoachInput] = useState('');
  const [coachLoading, setCoachLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [isEnhancingSummary, setIsEnhancingSummary] = useState(false);
  const [showLimitDialog, setShowLimitDialog] = useState(false);

  // Editor mode state (UI form vs YAML code)
  const [editorMode, setEditorMode] = useState<'ui' | 'yaml'>('ui');
  const [yamlContent, setYamlContent] = useState<string>('');
  const [yamlErrors, setYamlErrors] = useState<string[]>([]);

  // Tab Reordering
  const [draggedTab, setDraggedTab] = useState<string | null>(null);

  // Master Panel State
  const [showMasterPanel, setShowMasterPanel] = useState(false);
  const [masterPanelPosition, setMasterPanelPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [selectedMasterText, setSelectedMasterText] = useState('');
  const [activeInputRef, setActiveInputRef] = useState<HTMLInputElement | HTMLTextAreaElement | null>(null);

  // Import from Analysis State
  const [analysisDataAvailable, setAnalysisDataAvailable] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load persisted data on mount
  useEffect(() => {
    // Load Resume
    const storedResume = localStorage.getItem(RESUME_STORAGE_KEY);
    if (storedResume) {
      try {
        const normalized = yamlToResume(storedResume);
        setResume(normalized);
        setAnalysisDataAvailable(true);
      } catch (e) {
        console.error("Failed to load stored resume", e);
      }
    }

    // Load Job Description
    const storedJD = localStorage.getItem(JD_STORAGE_KEY);
    if (storedJD) {
      setJobDescription(storedJD);
    }
    setIsInitialized(true);
  }, []);

  // Autosave resume changes
  useEffect(() => {
    if (!isInitialized) return;
    if (resume && resume.cv.name) {
      localStorage.setItem(RESUME_STORAGE_KEY, JSON.stringify(resume));
    }
  }, [resume, isInitialized]);

  // Close options menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e_0: MouseEvent) => {
      setShowOptionsMenu(false);
      // Also close master panel if clicked outside (unless custom logic in MasterPanel stops propagation)
      if (showMasterPanel && !(e_0.target as HTMLElement).closest('.master-panel-container')) {
        setShowMasterPanel(false);
      }
    };
    if (showOptionsMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showOptionsMenu]);

  // Fetch themes on mount
  useEffect(() => {
    fetch(`${API_BASE}/resume/themes`).then(res => res.json()).then(data => setThemes(data.themes || [])).catch(console.error);
  }, []);

  // Initialize WebSocket for real-time preview
  useEffect(() => {
    const socket = new WebSocket(`${WS_BASE}/ws/preview`);
    socket.binaryType = 'arraybuffer';
    socket.onopen = () => {
      console.log('WebSocket Connected');
      setWsConnected(true);
      setWs(socket);
    };
    socket.onmessage = event => {
      try {
        if (event.data instanceof ArrayBuffer) {
          // Handle PDF binary data
          const blob = new Blob([event.data], {
            type: 'application/pdf'
          });
          const url = URL.createObjectURL(blob);
          setPreviewUrl(url);
          setPreviewLoading(false);
        } else if (typeof event.data === 'string') {
          // Handle JSON messages (e.g. errors)
          const data_0 = JSON.parse(event.data);
          if (data_0.status === 'success' && data_0.image) {
            // Fallback/Legacy handling if ever needed
            setPreviewUrl(data_0.image);
            setPreviewLoading(false);
          } else if (data_0.status === 'error') {
            console.error('WebSocket Preview Error:', data_0.message);
            setPreviewLoading(false);
          }
        }
      } catch (e_1) {
        console.error('Failed to parse WebSocket message', e_1);
        setPreviewLoading(false);
      }
    };
    socket.onclose = () => {
      console.log('WebSocket Disconnected');
      setWsConnected(false);
      setWs(null);
    };
    return () => {
      socket.close();
    };
  }, []);

  // Debounced preview update
  const updatePreview = useCallback(async () => {
    if (!resume.cv.name) return;
    setPreviewLoading(true);
    try {
      // Convert resume to YAML format
      const yamlContent_0 = resumeToYaml({
        ...resume,
        design: {
          ...resume.design,
          theme: resumeTheme as any
        }
      });
      const response = await fetch(`${API_BASE}/resume/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          yaml_content: yamlContent_0,
          theme: resumeTheme
        })
      });
      if (response.ok) {
        const blob_0 = await response.blob();
        const url_0 = URL.createObjectURL(blob_0);
        setPreviewUrl(url_0);
      }
    } catch (error) {
      console.error('Preview failed:', error);
    } finally {
      setPreviewLoading(false);
    }
  }, [resume, resumeTheme]);

  // Debounced score update
  const updateScore = useCallback(async () => {
    if (!resume.cv.name) return;
    setScoreLoading(true);
    try {
      const yamlContent_1 = resumeToYaml(resume);
      const response_0 = await fetch(`${API_BASE}/resume/analyze-yaml`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          yaml_content: yamlContent_1,
          job_description: jobDescription || null
        })
      });
      if (response_0.ok) {
        const data_1 = await response_0.json();
        setScore(data_1.score);
      }
    } catch (error_0) {
      console.error('Scoring failed:', error_0);
    } finally {
      setScoreLoading(false);
    }
  }, [resume, jobDescription]);

  // WebSocket update function
  const updatePreviewWS = useCallback(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    // Get YAML content based on editor mode
    const content = editorMode === 'yaml' ? yamlContent : resumeToYaml({
      ...resume,
      design: {
        ...resume.design,
        theme: resumeTheme as any
      }
    });

    // Only skip if truly empty
    if (!content || content.trim().length < 10) return;
    setPreviewLoading(true);
    ws.send(JSON.stringify({
      yaml_content: content,
      theme: resumeTheme
    }));
  }, [resume, resumeTheme, ws, editorMode, yamlContent]);

  // Update preview (Debounced) - Uses WS if available, else HTTP
  useEffect(() => {
    const delay = wsConnected ? 300 : 2000; // 300ms for near real-time WebSocket updates
    const timer = setTimeout(() => {
      if (wsConnected) {
        updatePreviewWS();
      } else {
        updatePreview();
      }
    }, delay);
    return () => clearTimeout(timer);
  }, [resume, resumeTheme, wsConnected, updatePreview, updatePreviewWS, editorMode, yamlContent]);

  // Track when user has made changes (for "Check Score" button hint)
  useEffect(() => {
    setHasChanges(true);
  }, [resume]);

  // Handle section change - optionally trigger score update
  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    // Don't auto-update score on section change - user clicks button when ready
  };

  // Download PDF
  const downloadPdf = async () => {
    setIsSaving(true);
    try {
      const yamlContent_2 = resumeToYaml({
        ...resume,
        design: {
          ...resume.design,
          theme: resumeTheme as any
        }
      });
      const response_1 = await fetch(`${API_BASE}/resume/render`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          yaml_content: yamlContent_2,
          theme: resumeTheme
        })
      });
      if (response_1.ok) {
        const blob_1 = await response_1.blob();
        const url_1 = URL.createObjectURL(blob_1);
        const a = document.createElement('a');
        a.href = url_1;
        a.download = `${resume.cv.name.replace(/\s+/g, '_')}_Resume.pdf`;
        a.click();
        URL.revokeObjectURL(url_1);
      }
    } catch (error_1) {
      console.error('Download failed:', error_1);
    } finally {
      setIsSaving(false);
    }
  };

  // Load template
  const loadTemplate = async () => {
    try {
      const response_2 = await fetch(`${API_BASE}/resume/template?name=${encodeURIComponent(resume.cv.name || 'Your Name')}`);
      const data_2 = await response_2.json();
      if (data_2.yaml) {
        setResume(yamlToResume(data_2.yaml));
      }
    } catch (error_2) {
      console.error('Failed to load template:', error_2);
    }
  };

  // Add experience entry
  const addExperience = () => {
    setResume(prev => ({
      ...prev,
      cv: {
        ...prev.cv,
        sections: {
          ...prev.cv.sections,
          Experience: [...(prev.cv.sections.Experience || []), createEmptyExperience()]
        }
      }
    }));
  };

  // Update experience entry
  const updateExperience = (id: string, field: keyof ExperienceEntry, value: any) => {
    setResume(prev_0 => ({
      ...prev_0,
      cv: {
        ...prev_0.cv,
        sections: {
          ...prev_0.cv.sections,
          Experience: (prev_0.cv.sections.Experience || []).map(exp => exp.id === id ? {
            ...exp,
            [field]: value
          } : exp)
        }
      }
    }));
  };

  // Remove experience entry
  const removeExperience = (id_0: string) => {
    setResume(prev_1 => ({
      ...prev_1,
      cv: {
        ...prev_1.cv,
        sections: {
          ...prev_1.cv.sections,
          Experience: (prev_1.cv.sections.Experience || []).filter(exp_0 => exp_0.id !== id_0)
        }
      }
    }));
  };

  // Add education entry
  const addEducation = () => {
    setResume(prev_2 => ({
      ...prev_2,
      cv: {
        ...prev_2.cv,
        sections: {
          ...prev_2.cv.sections,
          Education: [...(prev_2.cv.sections.Education || []), createEmptyEducation()]
        }
      }
    }));
  };

  // Update education entry
  const updateEducation = (id_1: string, field_0: keyof EducationEntry, value_0: any) => {
    setResume(prev_3 => ({
      ...prev_3,
      cv: {
        ...prev_3.cv,
        sections: {
          ...prev_3.cv.sections,
          Education: (prev_3.cv.sections.Education || []).map(edu => edu.id === id_1 ? {
            ...edu,
            [field_0]: value_0
          } : edu)
        }
      }
    }));
  };

  // Remove education entry
  const removeEducation = (id_2: string) => {
    setResume(prev_4 => ({
      ...prev_4,
      cv: {
        ...prev_4.cv,
        sections: {
          ...prev_4.cv.sections,
          Education: (prev_4.cv.sections.Education || []).filter(edu_0 => edu_0.id !== id_2)
        }
      }
    }));
  };

  // --- Projects Helpers ---
  const addProject = () => {
    setResume(prev_5 => ({
      ...prev_5,
      cv: {
        ...prev_5.cv,
        sections: {
          ...prev_5.cv.sections,
          Projects: [...(prev_5.cv.sections.Projects || []), createEmptyExperience()]
        }
      }
    }));
  };
  const updateProject = (id_3: string, field_1: keyof ExperienceEntry, value_1: any) => {
    setResume(prev_6 => ({
      ...prev_6,
      cv: {
        ...prev_6.cv,
        sections: {
          ...prev_6.cv.sections,
          Projects: (prev_6.cv.sections.Projects || []).map(p => p.id === id_3 ? {
            ...p,
            [field_1]: value_1
          } : p)
        }
      }
    }));
  };
  const removeProject = (id_4: string) => {
    setResume(prev_7 => ({
      ...prev_7,
      cv: {
        ...prev_7.cv,
        sections: {
          ...prev_7.cv.sections,
          Projects: (prev_7.cv.sections.Projects || []).filter(p_0 => p_0.id !== id_4)
        }
      }
    }));
  };

  // --- Publications Helpers ---
  const addPublication = () => {
    setResume(prev_8 => ({
      ...prev_8,
      cv: {
        ...prev_8.cv,
        sections: {
          ...prev_8.cv.sections,
          Publications: [...(prev_8.cv.sections.Publications || []), createEmptyPublicationEntry()]
        }
      }
    }));
  };
  const updatePublication = (id_5: string, field_2: keyof PublicationEntry, value_2: any) => {
    setResume(prev_9 => ({
      ...prev_9,
      cv: {
        ...prev_9.cv,
        sections: {
          ...prev_9.cv.sections,
          Publications: (prev_9.cv.sections.Publications || []).map(p_1 => p_1.id === id_5 ? {
            ...p_1,
            [field_2]: value_2
          } : p_1)
        }
      }
    }));
  };
  const removePublication = (id_6: string) => {
    setResume(prev_10 => ({
      ...prev_10,
      cv: {
        ...prev_10.cv,
        sections: {
          ...prev_10.cv.sections,
          Publications: (prev_10.cv.sections.Publications || []).filter(p_2 => p_2.id !== id_6)
        }
      }
    }));
  };

  // --- Extracurricular Helpers ---
  const addExtracurricular = () => {
    setResume(prev_11 => ({
      ...prev_11,
      cv: {
        ...prev_11.cv,
        sections: {
          ...prev_11.cv.sections,
          "Extracurricular Activities": [...(prev_11.cv.sections["Extracurricular Activities"] || []), createEmptyExperience()]
        }
      }
    }));
  };
  const updateExtracurricular = (id_7: string, field_3: keyof ExperienceEntry, value_3: any) => {
    setResume(prev_12 => ({
      ...prev_12,
      cv: {
        ...prev_12.cv,
        sections: {
          ...prev_12.cv.sections,
          "Extracurricular Activities": (prev_12.cv.sections["Extracurricular Activities"] || []).map(p_3 => p_3.id === id_7 ? {
            ...p_3,
            [field_3]: value_3
          } : p_3)
        }
      }
    }));
  };
  const removeExtracurricular = (id_8: string) => {
    setResume(prev_13 => ({
      ...prev_13,
      cv: {
        ...prev_13.cv,
        sections: {
          ...prev_13.cv.sections,
          "Extracurricular Activities": (prev_13.cv.sections["Extracurricular Activities"] || []).filter(p_4 => p_4.id !== id_8)
        }
      }
    }));
  };

  // Get score color
  const getScoreColor = (scoreValue: number) => {
    if (scoreValue >= 85) return 'text-green-400';
    if (scoreValue >= 70) return 'text-yellow-400';
    if (scoreValue >= 55) return 'text-orange-400';
    return 'text-red-400';
  };

  // Get section score and issues from score breakdown
  // --- Tab Reordering Handlers ---
  const handleTabDragStart = (e_2: React.DragEvent, section_0: string) => {
    setDraggedTab(section_0);
    e_2.dataTransfer.setData('text/plain', section_0);
    e_2.dataTransfer.effectAllowed = 'move';
  };
  const handleTabDragOver = (e_3: React.DragEvent) => {
    e_3.preventDefault();
    e_3.dataTransfer.dropEffect = 'move';
  };
  const handleTabDrop = (e_4: React.DragEvent, targetSection: string) => {
    e_4.preventDefault();
    const sourceSection = draggedTab;
    if (!sourceSection || sourceSection === targetSection) return;

    // "personal" and "design" are fixed
    if (sourceSection === 'personal' || sourceSection === 'design') return;
    if (targetSection === 'personal' || targetSection === 'design') return;
    const currentStoredOrder = resume.cv.section_order || ['Summary', 'Experience', 'Projects', 'Education', 'Skills', 'Publications', 'Extracurricular Activities'];
    const findKey = (id_9: string) => currentStoredOrder.find(k => k.toLowerCase() === id_9.toLowerCase());
    const sourceKey = findKey(sourceSection);
    const targetKey = findKey(targetSection);
    if (sourceKey && targetKey) {
      const newOrder = [...currentStoredOrder];
      const sIndex = newOrder.indexOf(sourceKey);
      const tIndex = newOrder.indexOf(targetKey);
      if (sIndex !== -1 && tIndex !== -1) {
        newOrder.splice(sIndex, 1);
        newOrder.splice(tIndex, 0, sourceKey);
        setResume(prev_14 => ({
          ...prev_14,
          cv: {
            ...prev_14.cv,
            section_order: newOrder
          }
        }));
      }
    }
    setDraggedTab(null);
  };
  const getSectionInfo = (sectionName: string) => {
    if (!score?.breakdown?.sections?.sections) return {
      score: 0,
      max: 0,
      issues: 0
    };
    const sectionMap: Record<string, string> = {
      'personal': 'contact_info',
      'experience': 'Experience',
      'education': 'Education',
      'skills': 'Skills',
      'projects': 'Projects',
      'publications': 'Publications',
      'extracurricular activities': 'Extracurricular Activities',
      'summary': 'Summary'
    };
    const key = sectionMap[sectionName];
    if (sectionName === 'personal') {
      const contactInfo = score?.breakdown?.contact_info;
      return {
        score: contactInfo?.score || 0,
        max: contactInfo?.max_score || 15,
        issues: contactInfo?.items ? Object.values(contactInfo.items).filter((item: any) => !item.found).length : 0
      };
    }
    const section_1 = score.breakdown.sections.sections[key];
    return {
      score: section_1?.score || 0,
      max: section_1?.max_score || 0,
      issues: section_1?.issues?.length || 0
    };
  };

  // Handle switching between UI and YAML editor modes
  const handleModeSwitch = (newMode: 'ui' | 'yaml') => {
    if (newMode === editorMode) return;
    if (newMode === 'yaml') {
      // UI → YAML: Generate YAML from current resume state
      // Use keepEmpty: true to preserve empty fields so user doesn't lose work in progress
      const yaml = resumeToYaml(resume, {
        keepEmpty: true
      });
      setYamlContent(yaml);
      setYamlErrors([]);
    } else {
      // YAML → UI: Parse YAML and populate resume state
      try {
        // Use createDefaultOnError: false to throw error on invalid YAML instead of resetting
        const parsedResume = yamlToResume(yamlContent, {
          createDefaultOnError: false
        });
        setResume(parsedResume);
        // Also update resume theme from parsed data if present
        if (parsedResume.design?.theme) {
          setResumeTheme(parsedResume.design.theme);
        }
        setYamlErrors([]);
      } catch (e_5) {
        console.error('Failed to parse YAML:', e_5);
        setYamlErrors(['Invalid YAML. Please fix errors before switching modes.', (e_5 as Error).message]);
        return; // Don't switch if YAML is invalid
      }
    }
    setEditorMode(newMode);
  };

  // Sync theme dropdown changes to YAML content when in YAML mode
  useEffect(() => {
    if (editorMode === 'yaml' && yamlContent) {
      try {
        // Parse current YAML, update theme, and re-serialize
        const parsedResume_0 = yamlToResume(yamlContent);
        if (parsedResume_0.design?.theme !== resumeTheme) {
          const updatedResume = {
            ...parsedResume_0,
            design: {
              ...parsedResume_0.design,
              theme: resumeTheme as any
            }
          };
          setYamlContent(resumeToYaml(updatedResume));
        }
      } catch {
        // Invalid YAML, ignore
      }
    }
  }, [resumeTheme, editorMode]);

  // Send message to AI Coach
  const sendCoachMessage = async () => {
    if (!coachInput.trim() || coachLoading) return;
    const userMessage = {
      role: 'user' as const,
      content: coachInput
    };
    setCoachMessages(prev_15 => [...prev_15, userMessage]);
    setCoachInput('');
    setCoachLoading(true);
    try {
      // Create a session if we don't have one
      let currentSessionId = sessionId;
      if (!currentSessionId) {
        // Create a quick session by doing a mini-analysis
        const yamlContent_3 = resumeToYaml(resume);
        const analyzeResponse = await fetch(`${API_BASE}/resume/editor-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            yaml_content: yamlContent_3,
            job_description: jobDescription || ''
          })
        });
        if (analyzeResponse.ok) {
          const data_3 = await analyzeResponse.json();
          currentSessionId = data_3.session_id;
          setSessionId(currentSessionId);
        }
      }

      // If still no session, use a fallback approach with direct feedback
      const feedbackBody = currentSessionId ? {
        session_id: currentSessionId,
        question: `[Currently editing: ${activeSection} section] ${coachInput}`,
        conversation_history: coachMessages
      } : {
        question: coachInput,
        context: `Resume content: ${resumeToYaml(resume)}\nCurrently editing: ${activeSection}`,
        conversation_history: coachMessages
      };
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };
      if (user) {
        const token = await user.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response_3 = await fetch(`${API_BASE}/feedback`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(feedbackBody)
      });
      if (response_3.status === 429) {
        setShowLimitDialog(true);
        return;
      }
      if (response_3.ok) {
        const data_4 = await response_3.json();
        setCoachMessages(prev_17 => [...prev_17, {
          role: 'assistant',
          content: data_4.answer || data_4.response || 'I apologize, I could not generate a response. Please try again.'
        }]);
      } else {
        throw new Error('Failed to get response');
      }
    } catch (error_3) {
      console.error('Coach error:', error_3);
      setCoachMessages(prev_16 => [...prev_16, {
        role: 'assistant',
        content: '⚠️ I had trouble connecting. Make sure your API keys are configured in backend/.env. Try uploading a resume on the dashboard first to establish a session.'
      }]);
    } finally {
      setCoachLoading(false);
    }
  };

  // Add missing keyword to skills
  const addKeywordToSkills = (keyword: string) => {
    updateResumeWithHistory(prev_18 => ({
      ...prev_18,
      cv: {
        ...prev_18.cv,
        sections: {
          ...prev_18.cv.sections,
          Skills: [...(prev_18.cv.sections.Skills || []), keyword]
        }
      }
    }));
    setHasChanges(true);
  };

  // Update resume with undo history
  const updateResumeWithHistory = (updater: (prev: ResumeData) => ResumeData) => {
    setUndoStack(prev_19 => [...prev_19.slice(-49), resume]);
    setRedoStack([]);
    setResume(updater);
  };

  // Undo function
  const undo = useCallback(() => {
    if (undoStack.length === 0) return;
    const previous = undoStack[undoStack.length - 1];
    setRedoStack(prev_20 => [...prev_20, resume]);
    setUndoStack(prev_21 => prev_21.slice(0, -1));
    setResume(previous);
  }, [undoStack, resume]);

  // Redo function
  const redo = useCallback(() => {
    if (redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    setUndoStack(prev_22 => [...prev_22, resume]);
    setRedoStack(prev_23 => prev_23.slice(0, -1));
    setResume(next);
  }, [redoStack, resume]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e_6: KeyboardEvent) => {
      if ((e_6.metaKey || e_6.ctrlKey) && e_6.key === 'z') {
        e_6.preventDefault();
        if (e_6.shiftKey) {
          redo();
        } else {
          undo();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  // Export as YAML
  const exportAsYaml = () => {
    const yamlContent_4 = resumeToYaml({
      ...resume,
      design: {
        ...resume.design,
        theme: resumeTheme as any
      }
    });
    const blob_2 = new Blob([yamlContent_4], {
      type: 'text/yaml'
    });
    const url_2 = URL.createObjectURL(blob_2);
    const a_0 = document.createElement('a');
    a_0.href = url_2;
    a_0.download = `${resume.cv.name.replace(/\s+/g, '_') || 'Resume'}.yaml`;
    a_0.click();
    URL.revokeObjectURL(url_2);
    setShowOptionsMenu(false);
  };

  // Export as JSON
  const exportAsJson = () => {
    const jsonContent = JSON.stringify(resume, null, 2);
    const blob_3 = new Blob([jsonContent], {
      type: 'application/json'
    });
    const url_3 = URL.createObjectURL(blob_3);
    const a_1 = document.createElement('a');
    a_1.href = url_3;
    a_1.download = `${resume.cv.name.replace(/\s+/g, '_') || 'Resume'}.json`;
    a_1.click();
    URL.revokeObjectURL(url_3);
    setShowOptionsMenu(false);
  };

  // Drag and drop handlers for Experience
  const handleDragStart = (e_7: React.DragEvent, id_10: string) => {
    setDraggedItemId(id_10);
    e_7.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e_8: React.DragEvent, id_11: string) => {
    e_8.preventDefault();
    if (draggedItemId !== id_11) {
      setDragOverItemId(id_11);
    }
  };
  const handleDragEnd = () => {
    setDraggedItemId(null);
    setDragOverItemId(null);
  };
  const handleDropExperience = (e_9: React.DragEvent, targetId: string) => {
    e_9.preventDefault();
    if (!draggedItemId || draggedItemId === targetId) return;
    const experiences = [...(resume.cv.sections.Experience || [])];
    const draggedIdx = experiences.findIndex(exp_1 => exp_1.id === draggedItemId);
    const targetIdx = experiences.findIndex(exp_2 => exp_2.id === targetId);
    if (draggedIdx === -1 || targetIdx === -1) return;
    const [draggedItem] = experiences.splice(draggedIdx, 1);
    experiences.splice(targetIdx, 0, draggedItem);
    updateResumeWithHistory(prev_24 => ({
      ...prev_24,
      cv: {
        ...prev_24.cv,
        sections: {
          ...prev_24.cv.sections,
          Experience: experiences
        }
      }
    }));
    handleDragEnd();
  };
  const handleDropEducation = (e_10: React.DragEvent, targetId_0: string) => {
    e_10.preventDefault();
    if (!draggedItemId || draggedItemId === targetId_0) return;
    const education = [...(resume.cv.sections.Education || [])];
    const draggedIdx_0 = education.findIndex(edu_1 => edu_1.id === draggedItemId);
    const targetIdx_0 = education.findIndex(edu_2 => edu_2.id === targetId_0);
    if (draggedIdx_0 === -1 || targetIdx_0 === -1) return;
    const [draggedItem_0] = education.splice(draggedIdx_0, 1);
    education.splice(targetIdx_0, 0, draggedItem_0);
    updateResumeWithHistory(prev_25 => ({
      ...prev_25,
      cv: {
        ...prev_25.cv,
        sections: {
          ...prev_25.cv.sections,
          Education: education
        }
      }
    }));
    handleDragEnd();
  };

  // AI Summary Enhancement with streaming
  const enhanceSummary = async () => {
    const currentSummary = (resume.cv.sections.Summary || [''])[0];
    const skills = resume.cv.sections.Skills?.join(', ') || '';
    setIsEnhancingSummary(true);
    try {
      const headers_0: HeadersInit = {
        'Content-Type': 'application/json'
      };
      if (user) {
        const token_0 = await user.getIdToken();
        headers_0['Authorization'] = `Bearer ${token_0}`;
      }
      const response_4 = await fetch(`${API_BASE}/enhance-summary`, {
        method: 'POST',
        headers: headers_0,
        body: JSON.stringify({
          current_summary: currentSummary,
          job_description: jobDescription,
          name: resume.cv.name,
          key_skills: skills
        })
      });
      if (response_4.status === 429) {
        setShowLimitDialog(true);
        return;
      }
      if (!response_4.ok) throw new Error('Enhancement failed');
      const reader = response_4.body?.getReader();
      if (!reader) throw new Error('No reader');
      const decoder = new TextDecoder();
      let enhancedText = '';
      while (true) {
        const {
          done,
          value: value_4
        } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value_4, {
          stream: true
        });
        enhancedText += chunk;

        // Update the summary in real-time (streaming effect)
        setResume(prev_26 => ({
          ...prev_26,
          cv: {
            ...prev_26.cv,
            sections: {
              ...prev_26.cv.sections,
              Summary: [enhancedText]
            }
          }
        }));
      }

      // Final update with history for undo support
      updateResumeWithHistory(prev_27 => ({
        ...prev_27,
        cv: {
          ...prev_27.cv,
          sections: {
            ...prev_27.cv.sections,
            Summary: [enhancedText]
          }
        }
      }));
    } catch (error_4) {
      console.error('Enhancement error:', error_4);
    } finally {
      setIsEnhancingSummary(false);
    }
  };

  // Prepare Tabs Order (Lifted out of JSX for cleaner syntax)
  const bodySections = (resume.cv.section_order || ['Summary', 'Experience', 'Projects', 'Education', 'Skills', 'Publications', 'Extracurricular Activities']).map(s => s.toLowerCase());
  const customKeys = Object.keys(resume.cv._customSectionTypes || {}).map(k_0 => k_0.toLowerCase());
  const visibleBodySections = [...bodySections];
  customKeys.forEach(k_1 => {
    if (!visibleBodySections.includes(k_1)) {
      visibleBodySections.push(k_1);
    }
  });
  const visibleTabs = ['personal', ...visibleBodySections, 'design'];
  return <div className={`min-h-screen pt-16 md:pt-32 ${isDarkMode ? 'bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900' : 'bg-gradient-to-br from-slate-50 via-purple-50 to-white'}`}>
            {/* Header */}
            <header className={`sticky top-2 md:top-4 z-40 mx-2 md:mx-4 mt-8 md:mt-0 mb-2 md:mb-4 rounded-xl md:rounded-2xl shadow-lg ${isDarkMode ? 'glass-panel' : 'bg-white/90 backdrop-blur-xl border border-slate-200'}`}>
                <div className="max-w-[1800px] mx-auto px-2 md:px-4 py-2 md:py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2 md:gap-4">
                        {/* Back Button - Icon only on mobile */}
                        <Link href="/dashboard" className={`flex items-center justify-center w-8 h-8 md:w-auto md:h-auto md:px-0 rounded-lg md:rounded-none transition-colors ${isDarkMode ? 'text-slate-400 hover:text-white md:bg-transparent bg-white/5' : 'text-slate-500 hover:text-slate-900 md:bg-transparent bg-slate-100'}`}>
                            <span className="md:hidden">←</span>
                            <span className="hidden md:inline">← Back</span>
                        </Link>

                        {/* Undo/Redo - Compact on mobile */}
                        <div className="flex items-center gap-0.5 md:gap-1">
                            <button onClick={undo} disabled={undoStack.length === 0} className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-lg text-xs md:text-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${isDarkMode ? 'bg-white/5 hover:bg-white/10 border border-white/10' : 'bg-white hover:bg-slate-50 border border-slate-200 text-slate-600'}`} title="Undo (Cmd+Z)">
                                ↶
                            </button>
                            <button onClick={redo} disabled={redoStack.length === 0} className={`w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-lg text-xs md:text-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${isDarkMode ? 'bg-white/5 hover:bg-white/10 border border-white/10' : 'bg-white hover:bg-slate-50 border border-slate-200 text-slate-600'}`} title="Redo (Cmd+Shift+Z)">
                                ↷
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center gap-1 md:gap-2">
                        {/* AI Coach Toggle */}
                        <button onClick={() => setShowCoach(!showCoach)} className={`w-8 h-8 md:w-auto md:h-auto md:px-3 md:py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${showCoach ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50' : isDarkMode ? 'bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300' : 'bg-white hover:bg-slate-50 border border-slate-200 text-slate-600'}`}>
                            <span>🤖</span>
                            <span className="hidden md:inline">AI Coach</span>
                        </button>



                        {/* Editor Mode Toggle - UI vs YAML */}
                        <div className={`flex rounded-lg overflow-hidden border ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
                            <button onClick={() => handleModeSwitch('ui')} className={`px-2 md:px-3 py-1.5 md:py-2 text-sm font-medium transition-all flex items-center gap-1.5 ${editorMode === 'ui' ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white' : isDarkMode ? 'bg-white/5 hover:bg-white/10 text-slate-300' : 'bg-slate-50 hover:bg-slate-100 text-slate-600'}`}>
                                <span>📝</span>
                                <span className="hidden md:inline">UI</span>
                            </button>
                            <button onClick={() => handleModeSwitch('yaml')} className={`px-2 md:px-3 py-1.5 md:py-2 text-sm font-medium transition-all flex items-center gap-1.5 ${editorMode === 'yaml' ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white' : isDarkMode ? 'bg-white/5 hover:bg-white/10 text-slate-300' : 'bg-slate-50 hover:bg-slate-100 text-slate-600'}`}>
                                <span>💻</span>
                                <span className="hidden md:inline">YAML</span>
                            </button>
                        </div>

                        {/* Theme Selector - Compact on mobile */}
                        <select value={resumeTheme} onChange={e_11 => setResumeTheme(e_11.target.value)} className={`px-1.5 md:px-3 py-1.5 md:py-2 rounded-lg text-xs md:text-sm focus:outline-none focus:border-purple-500 max-w-[80px] md:max-w-none ${isDarkMode ? 'bg-white/5 border border-white/10 text-white' : 'bg-white border border-slate-200 text-slate-700'}`}>
                            {themes.map(t => <option key={t.id} value={t.id} className={isDarkMode ? 'bg-slate-800' : 'bg-white'}>{t.name}</option>)}
                        </select>

                        {/* Desktop: Download PDF Button */}
                        <button onClick={downloadPdf} disabled={isSaving || !resume.cv.name} className="hidden md:flex px-3 py-2 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 items-center gap-1.5 text-white">
                            {isSaving ? '...' : <>📥 Download PDF</>}
                        </button>

                        {/* Mobile: Unified Download Dropdown */}
                        <div className="relative md:hidden">
                            <button onClick={e_12 => {
              e_12.stopPropagation();
              setShowOptionsMenu(!showOptionsMenu);
            }} disabled={isSaving || !resume.cv.name} className="px-2 py-1.5 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 rounded-lg text-xs font-semibold transition-all disabled:opacity-50 flex items-center gap-0.5 text-white">
                                📥 <span className="opacity-60">▼</span>
                            </button>
                            {showOptionsMenu && <div className={`absolute right-0 top-full mt-1 w-36 rounded-lg shadow-xl overflow-hidden z-50 ${isDarkMode ? 'bg-slate-800 border border-white/10' : 'bg-white border border-slate-200'}`}>
                                    <button onClick={() => {
                downloadPdf();
                setShowOptionsMenu(false);
              }} className={`w-full px-3 py-2.5 text-left text-sm transition-colors flex items-center gap-2 ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-50 text-slate-700'}`}>
                                        📄 PDF
                                    </button>
                                    <button onClick={() => {
                exportAsYaml();
                setShowOptionsMenu(false);
              }} className={`w-full px-3 py-2.5 text-left text-sm transition-colors flex items-center gap-2 ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-50 text-slate-700'}`}>
                                        📝 YAML
                                    </button>
                                    <button onClick={() => {
                exportAsJson();
                setShowOptionsMenu(false);
              }} className={`w-full px-3 py-2.5 text-left text-sm transition-colors flex items-center gap-2 ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-50 text-slate-700'}`}>
                                        📋 JSON
                                    </button>
                                </div>}
                        </div>

                        {/* Desktop: More Options Dropdown */}
                        <div className="relative hidden md:block">
                            <button onClick={e_13 => {
              e_13.stopPropagation();
              setShowOptionsMenu(!showOptionsMenu);
            }} className={`px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-1.5 ${isDarkMode ? 'bg-white/5 hover:bg-white/10 border border-white/10' : 'bg-white hover:bg-slate-50 border border-slate-200 text-slate-600'}`}>
                                <span>⚙️</span>
                                <span>More</span>
                                <span className="text-xs opacity-60">▼</span>
                            </button>
                            {showOptionsMenu && <div className={`absolute right-0 top-full mt-1 w-48 rounded-lg shadow-xl overflow-hidden z-50 ${isDarkMode ? 'bg-slate-800 border border-white/10' : 'bg-white border border-slate-200'}`}>
                                    <div className={`border-t ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
                                        <button onClick={exportAsYaml} className={`w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center gap-2 ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-50 text-slate-700'}`}>
                                            📄 Export as YAML
                                        </button>
                                        <button onClick={exportAsJson} className={`w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center gap-2 ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-50 text-slate-700'}`}>
                                            📋 Export as JSON
                                        </button>
                                    </div>
                                </div>}
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content - Split View (stacked on mobile) */}
            <div className="flex flex-col md:flex-row min-h-[calc(100vh-120px)] md:h-[calc(100vh-64px)]">
                {/* Left Panel - Editor */}
                {/* Left Panel - Editor */}
                <div className={`w-full md:w-1/2 md:min-h-0 overflow-y-auto ${editorMode === 'yaml' ? 'p-0' : 'p-3 md:p-6 pb-24 md:pb-40'} border-b md:border-b-0 md:border-r ${isDarkMode ? 'border-white/10' : 'border-slate-200 bg-white/50'}`}>

                    {/* YAML Editor Mode */}
                    {editorMode === 'yaml' && <div className="h-[65vh] md:h-full flex flex-col">
                            {/* YAML Editor Header */}
                            <div className={`flex items-center justify-between px-4 py-2 border-b ${isDarkMode ? 'border-white/10 bg-slate-800/50' : 'border-slate-200 bg-slate-50'}`}>
                                <div className="flex items-center gap-2">
                                    <span className="text-green-400">💻</span>
                                    <span className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>RenderCV YAML Editor</span>
                                    {wsConnected && <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">● Live</span>}
                                </div>
                                {yamlErrors.length > 0 && <span className="text-xs text-red-400 flex items-center gap-1">
                                        ⚠️ {yamlErrors.length} issue{yamlErrors.length > 1 ? 's' : ''}
                                    </span>}
                            </div>
                            {/* Monaco YAML Editor */}
                            <div className="h-full flex-1 md:min-h-0 relative">
                                <YamlEditor value={yamlContent} onChange={setYamlContent} onValidationChange={setYamlErrors} isDarkMode={isDarkMode} height="100%" />
                            </div>
                        </div>}

                    {/* UI Form Editor Mode */}
                    {editorMode === 'ui' && <div onDoubleClick={e_14 => {
          const target = e_14.target as HTMLElement;
          if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
            const input = target as HTMLInputElement | HTMLTextAreaElement;
            const text = input.value.substring(input.selectionStart || 0, input.selectionEnd || 0);
            if (text) {
              setMasterPanelPosition({
                x: e_14.clientX,
                y: e_14.clientY
              });
              setSelectedMasterText(text);
              setActiveInputRef(input);
              setShowMasterPanel(true);
            }
          }
        }} onMouseUp={e_15 => {
          // Trigger Master Panel on text selection
          const selection = window.getSelection();
          const text_0 = selection ? selection.toString() : '';
          if (text_0 && text_0.length > 0) {
            // Ensure we are selecting inside an input/textarea
            const target_0 = e_15.target as HTMLElement;
            if (target_0.tagName === 'INPUT' || target_0.tagName === 'TEXTAREA') {
              const input_0 = target_0 as HTMLInputElement | HTMLTextAreaElement;
              setMasterPanelPosition({
                x: e_15.clientX,
                y: e_15.clientY
              });
              setSelectedMasterText(text_0);
              setActiveInputRef(input_0);
              setShowMasterPanel(true);
            }
          }
        }} onTouchEnd={e_16 => {
          // Touch support for Master Panel
          setTimeout(() => {
            const selection_0 = window.getSelection();
            const text_1 = selection_0 ? selection_0.toString() : '';
            if (text_1 && text_1.length > 0) {
              const target_1 = e_16.target as HTMLElement;
              if (target_1.tagName === 'INPUT' || target_1.tagName === 'TEXTAREA') {
                const input_1 = target_1 as HTMLInputElement | HTMLTextAreaElement;
                // For mobile, positioning is handled by CSS (bottom dock), 
                // but we still pass coordinates just in case or for tablet centering
                const touch = e_16.changedTouches[0];
                setMasterPanelPosition({
                  x: touch.clientX,
                  y: touch.clientY
                });
                setSelectedMasterText(text_1);
                setActiveInputRef(input_1);
                setShowMasterPanel(true);
              }
            }
          }, 100); // Small delay to let selection settle
        }}>
                            {/* Section Tabs with Score Badges */}
                            <div className="flex gap-2 mb-6 overflow-x-auto pb-4">
                                {visibleTabs.map(section_2 => {
              const isCustom = Object.keys(resume.cv._customSectionTypes || {}).map(k_2 => k_2.toLowerCase()).includes(section_2);
              const info = getSectionInfo(section_2);
              let label = section_2.charAt(0).toUpperCase() + section_2.slice(1);
              if (section_2 === 'extracurricular activities') label = 'Extracurricular Activities';
              const originalKey = Object.keys(resume.cv._customSectionTypes || {}).find(k_3 => k_3.toLowerCase() === section_2);
              if (originalKey) label = originalKey;
              const canDrag = section_2 !== 'personal' && section_2 !== 'design';
              return <button key={section_2} draggable={canDrag} onDragStart={e_17 => canDrag && handleTabDragStart(e_17, section_2)} onDragOver={handleTabDragOver} onDrop={e_18 => canDrag && handleTabDrop(e_18, section_2)} onClick={() => handleSectionChange(section_2)} className={`theme-tab shrink-0 ${activeSection === section_2 ? 'active' : ''} ${draggedTab === section_2 ? 'opacity-50 border-dashed border-purple-400' : ''}`}>
                                            <span className="flex items-center gap-1.5">
                                                {canDrag && <span className="text-slate-400 cursor-grab active:cursor-grabbing text-xs">⋮⋮</span>}
                                                {isCustom ? '📑 ' : ''}
                                                {label}
                                            </span>
                                            {score && info.max > 0 && <span className={`text-xs px-1.5 py-0.5 rounded-full ${info.score >= info.max * 0.8 ? 'bg-green-500/20 text-green-600' : info.score >= info.max * 0.5 ? 'bg-yellow-500/20 text-yellow-600' : 'bg-red-500/20 text-red-600'}`}>
                                                    {info.score}/{info.max}
                                                </span>}
                                            {score && info.issues > 0 && <span className="text-xs bg-orange-500/20 text-orange-600 px-1.5 py-0.5 rounded-full">
                                                    ⚠️ {info.issues}
                                                </span>}
                                        </button>;
            })}
                            </div>

                            {/* Personal Info Section */}
                            {activeSection === 'personal' && <div className="space-y-4">
                                    <h2 className="theme-section-title">👤 Personal Information</h2>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                        <div>
                                            <label className="theme-label">Full Name *</label>
                                            <input type="text" value={resume.cv.name} onChange={e_19 => setResume(prev_28 => ({
                  ...prev_28,
                  cv: {
                    ...prev_28.cv,
                    name: e_19.target.value
                  }
                }))} className="theme-input" placeholder="John Doe" />
                                        </div>
                                        <div>
                                            <label className="theme-label">Email *</label>
                                            <input type="email" value={resume.cv.email} onChange={e_20 => setResume(prev_29 => ({
                  ...prev_29,
                  cv: {
                    ...prev_29.cv,
                    email: e_20.target.value
                  }
                }))} className="theme-input" placeholder="john@example.com" />
                                        </div>
                                        <div>
                                            <label className="theme-label">Phone</label>
                                            <div className="flex gap-2">
                                                <select className={`w-28 px-2 py-2 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-purple-500/50 appearance-none cursor-pointer ${isDarkMode ? 'bg-slate-800 border-slate-700 text-slate-200' : 'bg-white border-slate-200 text-slate-700'}`} value={(() => {
                    const phone = (resume.cv.phone || '').trim();
                    // Common codes + generic catch-all
                    const codes = ['+1', '+44', '+91', '+61', '+49', '+33', '+81', '+86', '+971', '+65'];
                    const found = codes.find(c => phone.startsWith(c));
                    return found || '+91';
                  })()} onChange={e_21 => {
                    const newCode = e_21.target.value;
                    const phone_0 = (resume.cv.phone || '').trim();
                    const codes_0 = ['+1', '+44', '+91', '+61', '+49', '+33', '+81', '+86', '+971', '+65'];
                    const oldCode = codes_0.find(c_0 => phone_0.startsWith(c_0)) || '';
                    let number = oldCode ? phone_0.substring(oldCode.length).trim() : phone_0;
                    setResume(prev_30 => ({
                      ...prev_30,
                      cv: {
                        ...prev_30.cv,
                        phone: `${newCode} ${number}`
                      }
                    }));
                  }}>
                                                    <option value="+91">🇮🇳 +91</option>
                                                    <option value="+1">🇺🇸 +1</option>
                                                    <option value="+44">🇬🇧 +44</option>
                                                    <option value="+61">🇦🇺 +61</option>
                                                    <option value="+49">🇩🇪 +49</option>
                                                    <option value="+33">🇫🇷 +33</option>
                                                    <option value="+81">🇯🇵 +81</option>
                                                    <option value="+86">🇨🇳 +86</option>
                                                    <option value="+971">🇦🇪 +971</option>
                                                    <option value="+65">🇸🇬 +65</option>
                                                </select>
                                                <input type="tel" value={(() => {
                    const phone_1 = (resume.cv.phone || '').trim();
                    const codes_1 = ['+1', '+44', '+91', '+61', '+49', '+33', '+81', '+86', '+971', '+65'];
                    const found_0 = codes_1.find(c_1 => phone_1.startsWith(c_1));
                    return found_0 ? phone_1.substring(found_0.length).trim() : phone_1;
                  })()} onChange={e_22 => {
                    const number_0 = e_22.target.value;
                    const phone_2 = (resume.cv.phone || '').trim();
                    const codes_2 = ['+1', '+44', '+91', '+61', '+49', '+33', '+81', '+86', '+971', '+65'];
                    const found_1 = codes_2.find(c_2 => phone_2.startsWith(c_2)) || '+91';
                    setResume(prev_31 => ({
                      ...prev_31,
                      cv: {
                        ...prev_31.cv,
                        phone: `${found_1} ${number_0}`
                      }
                    }));
                  }} className="theme-input flex-1" placeholder="9876543210" />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="theme-label">Location</label>
                                            <input type="text" value={resume.cv.location} onChange={e_23 => setResume(prev_32 => ({
                  ...prev_32,
                  cv: {
                    ...prev_32.cv,
                    location: e_23.target.value
                  }
                }))} className="theme-input" placeholder="San Francisco, CA" />
                                        </div>
                                    </div>

                                    {/* Dynamic Social Networks Section */}
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <label className="theme-label">Social Networks</label>
                                            <div className="relative">
                                                <select onChange={e_24 => {
                    const network = e_24.target.value as SupportedNetwork;
                    if (network && !resume.cv.social_networks.find(sn => sn.network === network)) {
                      setResume(prev_33 => ({
                        ...prev_33,
                        cv: {
                          ...prev_33.cv,
                          social_networks: [...prev_33.cv.social_networks, {
                            network,
                            username: ''
                          }]
                        }
                      }));
                    }
                    e_24.target.value = ''; // Reset selector
                  }} className={`px-3 py-1.5 rounded-lg text-sm cursor-pointer ${isDarkMode ? 'bg-purple-500/20 border border-purple-500/50 text-purple-300' : 'bg-purple-50 border border-purple-200 text-purple-700'}`} defaultValue="">
                                                    <option value="" disabled>+ Add Network</option>
                                                    {SUPPORTED_NETWORKS.filter(network_0 => !resume.cv.social_networks.find(sn_0 => sn_0.network === network_0)).map(network_1 => <option key={network_1} value={network_1} className={isDarkMode ? 'bg-slate-800' : 'bg-white'}>
                                                                {network_1}
                                                            </option>)}
                                                </select>
                                            </div>
                                        </div>

                                        {resume.cv.social_networks.length === 0 && <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                                No social networks added. Click "Add Network" to add LinkedIn, GitHub, Leetcode, YouTube, and more.
                                            </p>}

                                        {resume.cv.social_networks.map((sn_1, index) => <div key={sn_1.network} className={`flex items-center gap-2 p-3 rounded-lg ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                                                <span className={`w-24 text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                                    {sn_1.network}
                                                </span>
                                                <input type="text" value={sn_1.username} onChange={e_25 => setResume(prev_34 => ({
                  ...prev_34,
                  cv: {
                    ...prev_34.cv,
                    social_networks: prev_34.cv.social_networks.map((s_0, i) => i === index ? {
                      ...s_0,
                      username: e_25.target.value
                    } : s_0)
                  }
                }))} className="theme-input flex-1" placeholder={`Enter ${sn_1.network} username`} />
                                                <button onClick={() => setResume(prev_35 => ({
                  ...prev_35,
                  cv: {
                    ...prev_35.cv,
                    social_networks: prev_35.cv.social_networks.filter((_, i_0) => i_0 !== index)
                  }
                }))} className="text-red-400 hover:text-red-300 p-1.5 rounded hover:bg-red-500/10 transition-colors" title="Remove">
                                                    ✕
                                                </button>
                                            </div>)}
                                    </div>
                                </div>}

                            {/* Experience Section */}
                            {activeSection === 'experience' && <div className="space-y-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="theme-section-title">💼 Work Experience</h2>
                                        <button onClick={addExperience} className="px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/50 rounded-lg text-sm transition-colors">
                                            + Add Experience
                                        </button>
                                    </div>

                                    {(resume.cv.sections.Experience || []).map((exp_3, index_0) => <div key={exp_3.id} draggable onDragStart={e_26 => handleDragStart(e_26, exp_3.id)} onDragOver={e_27 => handleDragOver(e_27, exp_3.id)} onDrop={e_28 => handleDropExperience(e_28, exp_3.id)} onDragEnd={handleDragEnd} className={`p-4 rounded-lg border space-y-3 cursor-grab active:cursor-grabbing transition-all ${isDarkMode ? 'bg-white/5' : 'bg-slate-50'} ${draggedItemId === exp_3.id ? 'opacity-50 border-purple-500/50' : dragOverItemId === exp_3.id ? 'border-purple-400 bg-purple-500/10' : isDarkMode ? 'border-white/10 hover:border-white/20' : 'border-slate-200 hover:border-slate-300'}`}>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-slate-500 cursor-grab">⠿</span>
                                                    <span className="text-xs text-slate-400">Experience #{index_0 + 1}</span>
                                                </div>
                                                <button onClick={() => removeExperience(exp_3.id)} className="text-red-400 hover:text-red-300 text-sm">
                                                    Remove
                                                </button>
                                            </div>

                                            <div className="grid grid-cols-2 gap-3">
                                                <input type="text" value={exp_3.company} onChange={e_29 => updateExperience(exp_3.id, 'company', e_29.target.value)} className="theme-input" placeholder="Company Name" />
                                                <input type="text" value={exp_3.position} onChange={e_30 => updateExperience(exp_3.id, 'position', e_30.target.value)} className="theme-input" placeholder="Job Title" />
                                                <input type="text" value={exp_3.location} onChange={e_31 => updateExperience(exp_3.id, 'location', e_31.target.value)} className="theme-input" placeholder="Location" />
                                                <div className="flex gap-2">
                                                    <input type="text" value={exp_3.start_date} onChange={e_32 => updateExperience(exp_3.id, 'start_date', e_32.target.value)} className="flex-1 theme-input" placeholder="Start (YYYY-MM)" />
                                                    <input type="text" value={exp_3.end_date} onChange={e_33 => updateExperience(exp_3.id, 'end_date', e_33.target.value)} className="flex-1 theme-input" placeholder="End or 'present'" />
                                                </div>
                                            </div>

                                            <div>
                                                <label className="theme-label">Highlights (one per line)</label>
                                                <textarea value={exp_3.highlights.join('\n')} onChange={e_34 => updateExperience(exp_3.id, 'highlights', e_34.target.value.split('\n'))} className="w-full theme-input h-24 resize-none" placeholder="• Led development of...&#10;• Increased revenue by 20%...&#10;• Managed team of 5 engineers..." />
                                            </div>
                                        </div>)}

                                    {(resume.cv.sections.Experience || []).length === 0 && <div className="p-8 text-center text-slate-500">
                                            <p>No experience added yet.</p>
                                            <button onClick={addExperience} className="mt-2 text-purple-400 hover:text-purple-300">
                                                + Add your first experience
                                            </button>
                                        </div>}
                                </div>}

                            {/* Education Section */}
                            {activeSection === 'education' && <div className="space-y-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="theme-section-title">🎓 Education</h2>
                                        <button onClick={addEducation} className="px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/50 rounded-lg text-sm transition-colors">
                                            + Add Education
                                        </button>
                                    </div>

                                    {(resume.cv.sections.Education || []).map((edu_3, index_1) => <div key={edu_3.id} draggable onDragStart={e_35 => handleDragStart(e_35, edu_3.id)} onDragOver={e_36 => handleDragOver(e_36, edu_3.id)} onDrop={e_37 => handleDropEducation(e_37, edu_3.id)} onDragEnd={handleDragEnd} className={`p-4 rounded-lg border space-y-3 cursor-grab active:cursor-grabbing transition-all ${isDarkMode ? 'bg-white/5' : 'bg-slate-50'} ${draggedItemId === edu_3.id ? 'opacity-50 border-purple-500/50' : dragOverItemId === edu_3.id ? 'border-purple-400 bg-purple-500/10' : isDarkMode ? 'border-white/10 hover:border-white/20' : 'border-slate-200 hover:border-slate-300'}`}>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-slate-500 cursor-grab">⠿</span>
                                                    <span className="text-xs text-slate-400">Education #{index_1 + 1}</span>
                                                </div>
                                                <button onClick={() => removeEducation(edu_3.id)} className="text-red-400 hover:text-red-300 text-sm">
                                                    Remove
                                                </button>
                                            </div>

                                            <div className="grid grid-cols-2 gap-3">
                                                <input type="text" value={edu_3.institution} onChange={e_38 => updateEducation(edu_3.id, 'institution', e_38.target.value)} className="theme-input" placeholder="University Name" />
                                                <input type="text" value={edu_3.degree} onChange={e_39 => updateEducation(edu_3.id, 'degree', e_39.target.value)} className="theme-input" placeholder="Degree (Bachelor's, Master's)" />
                                                <input type="text" value={edu_3.area} onChange={e_40 => updateEducation(edu_3.id, 'area', e_40.target.value)} className="theme-input" placeholder="Field of Study" />
                                                <input type="text" value={edu_3.location} onChange={e_41 => updateEducation(edu_3.id, 'location', e_41.target.value)} className="theme-input" placeholder="Location" />
                                                <input type="text" value={edu_3.start_date} onChange={e_42 => updateEducation(edu_3.id, 'start_date', e_42.target.value)} className="theme-input" placeholder="Start (YYYY-MM)" />
                                                <input type="text" value={edu_3.end_date} onChange={e_43 => updateEducation(edu_3.id, 'end_date', e_43.target.value)} className="theme-input" placeholder="End (YYYY-MM)" />
                                            </div>

                                            <div>
                                                <label className="theme-label">Highlights (one per line)</label>
                                                <textarea value={edu_3.highlights.join('\n')} onChange={e_44 => updateEducation(edu_3.id, 'highlights', e_44.target.value.split('\n'))} className="w-full theme-input h-24 resize-none" placeholder="• GPA: 3.9/4.0&#10;• Teaching Assistant for CS101&#10;• Dean's List" />
                                            </div>
                                        </div>)}

                                    {(resume.cv.sections.Education || []).length === 0 && <div className="p-8 text-center text-slate-500">
                                            <p>No education added yet.</p>
                                            <button onClick={addEducation} className="mt-2 text-purple-400 hover:text-purple-300">
                                                + Add your education
                                            </button>
                                        </div>}
                                </div>}

                            {/* Skills Section */}
                            {activeSection === 'skills' && <div className="space-y-4">
                                    <h2 className="theme-section-title mb-4">🛠️ Skills</h2>

                                    <div>
                                        <label className="theme-label">
                                            Skills (one category per line, e.g., "Languages: Python, JavaScript, TypeScript")
                                        </label>
                                        <textarea value={(resume.cv.sections.Skills || []).join('\n')} onChange={e_45 => setResume(prev_36 => ({
                ...prev_36,
                cv: {
                  ...prev_36.cv,
                  sections: {
                    ...prev_36.cv.sections,
                    Skills: e_45.target.value.split('\n')
                  }
                }
              }))} className="w-full theme-input h-48 resize-none" placeholder="Programming Languages: Python, JavaScript, TypeScript&#10;Frameworks: React, Node.js, FastAPI, Django&#10;Cloud: AWS, GCP, Docker, Kubernetes&#10;Databases: PostgreSQL, MongoDB, Redis&#10;Tools: Git, CI/CD, Terraform" />
                                    </div>

                                    {/* AI Suggestions based on score */}
                                    {score?.breakdown?.keywords?.missing_keywords?.length > 0 && <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                                            <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>💡 Suggested Skills (from JD)</p>
                                            <div className="flex flex-wrap gap-2">
                                                {score.breakdown.keywords.missing_keywords.slice(0, 8).map((kw: string, i_1: number) => <button key={i_1} onClick={() => setResume(prev_37 => ({
                  ...prev_37,
                  cv: {
                    ...prev_37.cv,
                    sections: {
                      ...prev_37.cv.sections,
                      Skills: [...(prev_37.cv.sections.Skills || []), kw]
                    }
                  }
                }))} className={`px-2 py-1 bg-purple-500/20 hover:bg-purple-500/40 text-xs rounded-full transition-colors ${isDarkMode ? 'text-purple-200' : 'text-purple-800'}`}>
                                                        + {kw}
                                                    </button>)}
                                            </div>
                                        </div>}
                                </div>}

                            {/* Projects Section */}
                            {activeSection === 'projects' && <div className="space-y-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="theme-section-title">🚀 Projects</h2>
                                        <button onClick={addProject} className="px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/50 rounded-lg text-sm transition-colors">+ Add Project</button>
                                    </div>
                                    {(resume.cv.sections.Projects || []).map((proj, index_2) => <div key={proj.id} className={`p-4 rounded-lg border space-y-3 ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                                            <div className="flex justify-between"><span className="text-xs text-slate-400">Project #{index_2 + 1}</span><button onClick={() => removeProject(proj.id)} className="text-red-400 hover:text-red-300 text-sm">Remove</button></div>
                                            <div className="grid grid-cols-2 gap-3">
                                                <input type="text" value={proj.company} onChange={e_46 => updateProject(proj.id, 'company', e_46.target.value)} className="theme-input" placeholder="Project Name" />
                                                <input type="text" value={proj.position} onChange={e_47 => updateProject(proj.id, 'position', e_47.target.value)} className="theme-input" placeholder="Your Role (e.g. Lead Developer)" />
                                                <input type="text" value={proj.start_date} onChange={e_48 => updateProject(proj.id, 'start_date', e_48.target.value)} className="theme-input" placeholder="Start (YYYY-MM)" />
                                                <input type="text" value={proj.end_date} onChange={e_49 => updateProject(proj.id, 'end_date', e_49.target.value)} className="theme-input" placeholder="End" />
                                            </div>
                                            <input type="text" value={proj.location} onChange={e_50 => updateProject(proj.id, 'location', e_50.target.value)} className="theme-input" placeholder="Location or URL" />
                                            <textarea value={proj.highlights.join('\n')} onChange={e_51 => updateProject(proj.id, 'highlights', e_51.target.value.split('\n'))} className="w-full theme-input h-24 resize-none" placeholder="• Key achievement 1...&#10;• Key achievement 2..." />
                                        </div>)}
                                    {(resume.cv.sections.Projects || []).length === 0 && <div className="p-8 text-center text-slate-500"><p>No projects added.</p><button onClick={addProject} className="mt-2 text-purple-400">+ Add Project</button></div>}
                                </div>}

                            {/* Publications Section */}
                            {activeSection === 'publications' && <div className="space-y-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="theme-section-title">📚 Publications</h2>
                                        <button onClick={addPublication} className="px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/50 rounded-lg text-sm transition-colors">+ Add Publication</button>
                                    </div>
                                    {(resume.cv.sections.Publications || []).map((pub, index_3) => <div key={pub.id} className={`p-4 rounded-lg border space-y-3 ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                                            <div className="flex justify-between"><span className="text-xs text-slate-400">Publication #{index_3 + 1}</span><button onClick={() => removePublication(pub.id)} className="text-red-400 hover:text-red-300 text-sm">Remove</button></div>
                                            <input type="text" value={pub.title} onChange={e_52 => updatePublication(pub.id, 'title', e_52.target.value)} className="theme-input" placeholder="Publication Title" />
                                            <input type="text" value={pub.authors.join(', ')} onChange={e_53 => updatePublication(pub.id, 'authors', e_53.target.value.split(',').map(s_1 => s_1.trim()))} className="theme-input" placeholder="Authors (comma separated)" />
                                            <div className="grid grid-cols-2 gap-3">
                                                <input type="text" value={pub.date} onChange={e_54 => updatePublication(pub.id, 'date', e_54.target.value)} className="theme-input" placeholder="Date (YYYY-MM)" />
                                                <input type="text" value={pub.journal} onChange={e_55 => updatePublication(pub.id, 'journal', e_55.target.value)} className="theme-input" placeholder="Journal/Conference" />
                                            </div>
                                            <input type="text" value={pub.doi || pub.url} onChange={e_56 => updatePublication(pub.id, 'url', e_56.target.value)} className="theme-input" placeholder="URL or DOI" />
                                        </div>)}
                                    {(resume.cv.sections.Publications || []).length === 0 && <div className="p-8 text-center text-slate-500"><p>No publications.</p><button onClick={addPublication} className="mt-2 text-purple-400">+ Add Publication</button></div>}
                                </div>}

                            {/* Extracurricular Activities Section */}
                            {activeSection === 'extracurricular activities' && <div className="space-y-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="theme-section-title">🏆 Extracurricular Activities</h2>
                                        <button onClick={addExtracurricular} className="px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/50 rounded-lg text-sm transition-colors">+ Add Activity</button>
                                    </div>
                                    {(resume.cv.sections["Extracurricular Activities"] || []).map((act, index_4) => <div key={act.id} className={`p-4 rounded-lg border space-y-3 ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                                            <div className="flex justify-between"><span className="text-xs text-slate-400">Activity #{index_4 + 1}</span><button onClick={() => removeExtracurricular(act.id)} className="text-red-400 hover:text-red-300 text-sm">Remove</button></div>
                                            <div className="grid grid-cols-2 gap-3">
                                                <input type="text" value={act.company} onChange={e_57 => updateExtracurricular(act.id, 'company', e_57.target.value)} className="theme-input" placeholder="Organization/Group" />
                                                <input type="text" value={act.position} onChange={e_58 => updateExtracurricular(act.id, 'position', e_58.target.value)} className="theme-input" placeholder="Role/Position" />
                                                <input type="text" value={act.start_date} onChange={e_59 => updateExtracurricular(act.id, 'start_date', e_59.target.value)} className="theme-input" placeholder="Start" />
                                                <input type="text" value={act.end_date} onChange={e_60 => updateExtracurricular(act.id, 'end_date', e_60.target.value)} className="theme-input" placeholder="End" />
                                            </div>
                                            <textarea value={act.highlights.join('\n')} onChange={e_61 => updateExtracurricular(act.id, 'highlights', e_61.target.value.split('\n'))} className="w-full theme-input h-24 resize-none" placeholder="• Description of involvement..." />
                                        </div>)}
                                    {(resume.cv.sections["Extracurricular Activities"] || []).length === 0 && <div className="p-8 text-center text-slate-500"><p>No extracurricular activities.</p><button onClick={addExtracurricular} className="mt-2 text-purple-400">+ Add Activity</button></div>}
                                </div>}

                            {/* Summary Section */}
                            {activeSection === 'summary' && <div className="space-y-4">
                                    <h2 className="theme-section-title mb-4">📝 Professional Summary</h2>

                                    <div className="relative">
                                        <label className="theme-label">
                                            Write a compelling summary (3-4 sentences highlighting your key skills and experience)
                                        </label>
                                        <div className="relative">
                                            <textarea value={(resume.cv.sections.Summary || [''])[0]} onChange={e_62 => setResume(prev_38 => ({
                  ...prev_38,
                  cv: {
                    ...prev_38.cv,
                    sections: {
                      ...prev_38.cv.sections,
                      Summary: [e_62.target.value]
                    }
                  }
                }))} disabled={isEnhancingSummary} className={`theme-input h-40 resize-none pb-12 ${isEnhancingSummary ? 'opacity-70' : ''}`} placeholder="Results-driven software engineer with 5+ years of experience building scalable web applications. Expert in React, Node.js, and cloud technologies. Passionate about creating exceptional user experiences and mentoring junior developers." />

                                            {/* Enhance with AI Button */}
                                            <button onClick={enhanceSummary} disabled={isEnhancingSummary} className={`absolute bottom-3 right-3 px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${isEnhancingSummary ? 'bg-purple-500/40 text-purple-200 cursor-wait' : 'bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40'}`}>
                                                {isEnhancingSummary ? <>
                                                        <span className="inline-block w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                                        Enhancing...
                                                    </> : <>
                                                        <span>✨</span>
                                                        Enhance with AI
                                                    </>}
                                            </button>
                                        </div>

                                        {/* Tips */}
                                        <div className="mt-3 text-xs text-slate-500 flex items-center gap-2">
                                            <span>💡</span>
                                            <span>Tip: Add a job description above for better keyword optimization</span>
                                        </div>
                                    </div>
                                </div>}

                            {/* Design Section */}
                            {activeSection === 'design' && <div className="space-y-6">
                                    <h2 className="theme-section-title mb-4">🎨 Design & Styling</h2>
                                    <p className={`text-xs mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Customize every aspect of your resume. Leave fields empty for defaults.</p>

                                    {/* Page Settings */}
                                    <details className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'}`} open>
                                        <summary className={`text-sm font-semibold cursor-pointer ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>📄 Page Settings</summary>
                                        <div className="mt-4 grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="theme-label">Page Size</label>
                                                <select value={resume.design.page?.size || 'us-letter'} onChange={e_63 => setResume(prev_39 => ({
                    ...prev_39,
                    design: {
                      ...prev_39.design,
                      page: {
                        ...prev_39.design.page,
                        size: e_63.target.value as 'us-letter' | 'a4'
                      }
                    }
                  }))} className="theme-input w-full">
                                                    <option value="us-letter" className={isDarkMode ? 'bg-slate-800' : 'bg-white'}>US Letter</option>
                                                    <option value="a4" className={isDarkMode ? 'bg-slate-800' : 'bg-white'}>A4</option>
                                                </select>
                                            </div>
                                            {['top_margin', 'bottom_margin', 'left_margin', 'right_margin'].map(key_0 => <div key={key_0}>
                                                    <label className="theme-label capitalize">{key_0.replace(/_/g, ' ')}</label>
                                                    <input type="text" value={(resume.design.page as any)?.[key_0] || ''} onChange={e_64 => setResume(prev_40 => ({
                    ...prev_40,
                    design: {
                      ...prev_40.design,
                      page: {
                        ...prev_40.design.page,
                        [key_0]: e_64.target.value
                      }
                    }
                  }))} className="theme-input w-full" placeholder="2cm" />
                                                </div>)}
                                            <div className="col-span-2 flex gap-4">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input type="checkbox" checked={resume.design.page?.show_footer || false} onChange={e_65 => setResume(prev_41 => ({
                      ...prev_41,
                      design: {
                        ...prev_41.design,
                        page: {
                          ...prev_41.design.page,
                          show_footer: e_65.target.checked
                        }
                      }
                    }))} className="w-4 h-4 rounded" />
                                                    <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Show Footer</span>
                                                </label>
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input type="checkbox" checked={resume.design.page?.show_top_note || false} onChange={e_66 => setResume(prev_42 => ({
                      ...prev_42,
                      design: {
                        ...prev_42.design,
                        page: {
                          ...prev_42.design.page,
                          show_top_note: e_66.target.checked
                        }
                      }
                    }))} className="w-4 h-4 rounded" />
                                                    <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Show Top Note</span>
                                                </label>
                                            </div>
                                        </div>
                                    </details>

                                    {/* Colors Settings */}
                                    <details className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                                        <summary className={`text-sm font-semibold cursor-pointer ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>🎨 Colors</summary>
                                        <p className={`text-xs mt-2 mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Use RGB format: rgb(0, 79, 144)</p>
                                        <div className="grid grid-cols-2 gap-4">
                                            {['body', 'name', 'headline', 'connections', 'section_titles', 'links', 'footer', 'top_note'].map(key_1 => <div key={key_1}>
                                                    <label className="theme-label capitalize">{key_1.replace(/_/g, ' ')}</label>
                                                    <input type="text" value={(resume.design.colors as any)?.[key_1] || ''} onChange={e_67 => setResume(prev_43 => ({
                    ...prev_43,
                    design: {
                      ...prev_43.design,
                      colors: {
                        ...prev_43.design.colors,
                        [key_1]: e_67.target.value
                      }
                    }
                  }))} className="theme-input w-full" placeholder="rgb(0, 79, 144)" />
                                                </div>)}
                                        </div>
                                    </details>

                                    {/* Typography Settings */}
                                    <details className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                                        <summary className={`text-sm font-semibold cursor-pointer ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>🔤 Typography</summary>
                                        <div className="mt-4 grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="theme-label">Body Font Family</label>
                                                <input type="text" value={resume.design.typography?.font_family?.body || ''} onChange={e_68 => setResume(prev_44 => ({
                    ...prev_44,
                    design: {
                      ...prev_44.design,
                      typography: {
                        ...prev_44.design.typography,
                        font_family: {
                          ...prev_44.design.typography?.font_family,
                          body: e_68.target.value
                        }
                      }
                    }
                  }))} className="theme-input w-full" placeholder="Source Sans 3" />
                                            </div>
                                            <div>
                                                <label className="theme-label">Body Font Size</label>
                                                <input type="text" value={resume.design.typography?.font_size?.body || ''} onChange={e_69 => setResume(prev_45 => ({
                    ...prev_45,
                    design: {
                      ...prev_45.design,
                      typography: {
                        ...prev_45.design.typography,
                        font_size: {
                          ...prev_45.design.typography?.font_size,
                          body: e_69.target.value
                        }
                      }
                    }
                  }))} className="theme-input w-full" placeholder="10pt" />
                                            </div>
                                            <div>
                                                <label className="theme-label">Line Spacing</label>
                                                <input type="text" value={resume.design.typography?.line_spacing || ''} onChange={e_70 => setResume(prev_46 => ({
                    ...prev_46,
                    design: {
                      ...prev_46.design,
                      typography: {
                        ...prev_46.design.typography,
                        line_spacing: e_70.target.value
                      }
                    }
                  }))} className="theme-input w-full" placeholder="0.6em" />
                                            </div>
                                            <div>
                                                <label className="theme-label">Alignment</label>
                                                <select value={resume.design.typography?.alignment || 'justified'} onChange={e_71 => setResume(prev_47 => ({
                    ...prev_47,
                    design: {
                      ...prev_47.design,
                      typography: {
                        ...prev_47.design.typography,
                        alignment: e_71.target.value as 'justified' | 'left'
                      }
                    }
                  }))} className="theme-input w-full">
                                                    <option value="justified" className={isDarkMode ? 'bg-slate-800' : 'bg-white'}>Justified</option>
                                                    <option value="left" className={isDarkMode ? 'bg-slate-800' : 'bg-white'}>Left</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="theme-label">Name Font Size</label>
                                                <input type="text" value={resume.design.typography?.font_size?.name || ''} onChange={e_72 => setResume(prev_48 => ({
                    ...prev_48,
                    design: {
                      ...prev_48.design,
                      typography: {
                        ...prev_48.design.typography,
                        font_size: {
                          ...prev_48.design.typography?.font_size,
                          name: e_72.target.value
                        }
                      }
                    }
                  }))} className="theme-input w-full" placeholder="30pt" />
                                            </div>
                                            <div>
                                                <label className="theme-label">Section Titles Size</label>
                                                <input type="text" value={resume.design.typography?.font_size?.section_titles || ''} onChange={e_73 => setResume(prev_49 => ({
                    ...prev_49,
                    design: {
                      ...prev_49.design,
                      typography: {
                        ...prev_49.design.typography,
                        font_size: {
                          ...prev_49.design.typography?.font_size,
                          section_titles: e_73.target.value
                        }
                      }
                    }
                  }))} className="theme-input w-full" placeholder="1.4em" />
                                            </div>
                                        </div>
                                    </details>

                                    {/* Links Settings */}
                                    <details className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                                        <summary className={`text-sm font-semibold cursor-pointer ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>🔗 Links</summary>
                                        <div className="mt-4 flex gap-6">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="checkbox" checked={resume.design.links?.underline || false} onChange={e_74 => setResume(prev_50 => ({
                    ...prev_50,
                    design: {
                      ...prev_50.design,
                      links: {
                        ...prev_50.design.links,
                        underline: e_74.target.checked
                      }
                    }
                  }))} className="w-4 h-4 rounded" />
                                                <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Underline Links</span>
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="checkbox" checked={resume.design.links?.show_external_link_icon || false} onChange={e_75 => setResume(prev_51 => ({
                    ...prev_51,
                    design: {
                      ...prev_51.design,
                      links: {
                        ...prev_51.design.links,
                        show_external_link_icon: e_75.target.checked
                      }
                    }
                  }))} className="w-4 h-4 rounded" />
                                                <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Show External Link Icon</span>
                                            </label>
                                        </div>
                                    </details>

                                    {/* Header Settings */}
                                    <details className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                                        <summary className={`text-sm font-semibold cursor-pointer ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>📝 Header</summary>
                                        <div className="mt-4 grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="theme-label">Header Alignment</label>
                                                <select value={resume.design.header?.alignment || 'center'} onChange={e_76 => setResume(prev_52 => ({
                    ...prev_52,
                    design: {
                      ...prev_52.design,
                      header: {
                        ...prev_52.design.header,
                        alignment: e_76.target.value as 'left' | 'center' | 'right'
                      }
                    }
                  }))} className="theme-input w-full">
                                                    <option value="left" className={isDarkMode ? 'bg-slate-800' : 'bg-white'}>Left</option>
                                                    <option value="center" className={isDarkMode ? 'bg-slate-800' : 'bg-white'}>Center</option>
                                                    <option value="right" className={isDarkMode ? 'bg-slate-800' : 'bg-white'}>Right</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="theme-label">Photo Width</label>
                                                <input type="text" value={resume.design.header?.photo_width || ''} onChange={e_77 => setResume(prev_53 => ({
                    ...prev_53,
                    design: {
                      ...prev_53.design,
                      header: {
                        ...prev_53.design.header,
                        photo_width: e_77.target.value
                      }
                    }
                  }))} className="theme-input w-full" placeholder="3.5cm" />
                                            </div>
                                            <div>
                                                <label className="theme-label">Space Below Name</label>
                                                <input type="text" value={resume.design.header?.space_below_name || ''} onChange={e_78 => setResume(prev_54 => ({
                    ...prev_54,
                    design: {
                      ...prev_54.design,
                      header: {
                        ...prev_54.design.header,
                        space_below_name: e_78.target.value
                      }
                    }
                  }))} className="theme-input w-full" placeholder="0.7cm" />
                                            </div>
                                            <div>
                                                <label className="theme-label">Space Below Connections</label>
                                                <input type="text" value={resume.design.header?.space_below_connections || ''} onChange={e_79 => setResume(prev_55 => ({
                    ...prev_55,
                    design: {
                      ...prev_55.design,
                      header: {
                        ...prev_55.design.header,
                        space_below_connections: e_79.target.value
                      }
                    }
                  }))} className="theme-input w-full" placeholder="0.7cm" />
                                            </div>
                                            <div className="col-span-2">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input type="checkbox" checked={resume.design.header?.connections?.show_icons !== false} onChange={e_80 => setResume(prev_56 => ({
                      ...prev_56,
                      design: {
                        ...prev_56.design,
                        header: {
                          ...prev_56.design.header,
                          connections: {
                            ...prev_56.design.header?.connections,
                            show_icons: e_80.target.checked
                          }
                        }
                      }
                    }))} className="w-4 h-4 rounded" />
                                                    <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Show Icons for Connections</span>
                                                </label>
                                            </div>
                                        </div>
                                    </details>

                                    {/* Section Titles Settings */}
                                    <details className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                                        <summary className={`text-sm font-semibold cursor-pointer ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>📌 Section Titles</summary>
                                        <div className="mt-4 grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="theme-label">Style Type</label>
                                                <select value={resume.design.section_titles?.type || 'with_partial_line'} onChange={e_81 => setResume(prev_57 => ({
                    ...prev_57,
                    design: {
                      ...prev_57.design,
                      section_titles: {
                        ...prev_57.design.section_titles,
                        type: e_81.target.value as any
                      }
                    }
                  }))} className="theme-input w-full">
                                                    <option value="with_partial_line" className={isDarkMode ? 'bg-slate-800' : 'bg-white'}>With Partial Line</option>
                                                    <option value="with_full_line" className={isDarkMode ? 'bg-slate-800' : 'bg-white'}>With Full Line</option>
                                                    <option value="no_line" className={isDarkMode ? 'bg-slate-800' : 'bg-white'}>No Line</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="theme-label">Line Thickness</label>
                                                <input type="text" value={resume.design.section_titles?.line_thickness || ''} onChange={e_82 => setResume(prev_58 => ({
                    ...prev_58,
                    design: {
                      ...prev_58.design,
                      section_titles: {
                        ...prev_58.design.section_titles,
                        line_thickness: e_82.target.value
                      }
                    }
                  }))} className="theme-input w-full" placeholder="0.5pt" />
                                            </div>
                                            <div>
                                                <label className="theme-label">Space Above</label>
                                                <input type="text" value={resume.design.section_titles?.space_above || ''} onChange={e_83 => setResume(prev_59 => ({
                    ...prev_59,
                    design: {
                      ...prev_59.design,
                      section_titles: {
                        ...prev_59.design.section_titles,
                        space_above: e_83.target.value
                      }
                    }
                  }))} className="theme-input w-full" placeholder="0.5cm" />
                                            </div>
                                            <div>
                                                <label className="theme-label">Space Below</label>
                                                <input type="text" value={resume.design.section_titles?.space_below || ''} onChange={e_84 => setResume(prev_60 => ({
                    ...prev_60,
                    design: {
                      ...prev_60.design,
                      section_titles: {
                        ...prev_60.design.section_titles,
                        space_below: e_84.target.value
                      }
                    }
                  }))} className="theme-input w-full" placeholder="0.3cm" />
                                            </div>
                                        </div>
                                    </details>

                                    {/* Entries & Sections Settings */}
                                    <details className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                                        <summary className={`text-sm font-semibold cursor-pointer ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>📋 Entries & Highlights</summary>
                                        <div className="mt-4 grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="theme-label">Date Column Width</label>
                                                <input type="text" value={resume.design.entries?.date_and_location_width || ''} onChange={e_85 => setResume(prev_61 => ({
                    ...prev_61,
                    design: {
                      ...prev_61.design,
                      entries: {
                        ...prev_61.design.entries,
                        date_and_location_width: e_85.target.value
                      }
                    }
                  }))} className="theme-input w-full" placeholder="4.15cm" />
                                            </div>
                                            <div>
                                                <label className="theme-label">Side Space</label>
                                                <input type="text" value={resume.design.entries?.side_space || ''} onChange={e_86 => setResume(prev_62 => ({
                    ...prev_62,
                    design: {
                      ...prev_62.design,
                      entries: {
                        ...prev_62.design.entries,
                        side_space: e_86.target.value
                      }
                    }
                  }))} className="theme-input w-full" placeholder="0.2cm" />
                                            </div>
                                            <div>
                                                <label className="theme-label">Bullet Character</label>
                                                <input type="text" value={resume.design.entries?.highlights?.bullet || ''} onChange={e_87 => setResume(prev_63 => ({
                    ...prev_63,
                    design: {
                      ...prev_63.design,
                      entries: {
                        ...prev_63.design.entries,
                        highlights: {
                          ...prev_63.design.entries?.highlights,
                          bullet: e_87.target.value
                        }
                      }
                    }
                  }))} className="theme-input w-full" placeholder="•" />
                                            </div>
                                            <div>
                                                <label className="theme-label">Nested Bullet</label>
                                                <input type="text" value={resume.design.entries?.highlights?.nested_bullet || ''} onChange={e_88 => setResume(prev_64 => ({
                    ...prev_64,
                    design: {
                      ...prev_64.design,
                      entries: {
                        ...prev_64.design.entries,
                        highlights: {
                          ...prev_64.design.entries?.highlights,
                          nested_bullet: e_88.target.value
                        }
                      }
                    }
                  }))} className="theme-input w-full" placeholder="•" />
                                            </div>
                                            <div>
                                                <label className="theme-label">Space Between Entries</label>
                                                <input type="text" value={resume.design.sections?.space_between_regular_entries || ''} onChange={e_89 => setResume(prev_65 => ({
                    ...prev_65,
                    design: {
                      ...prev_65.design,
                      sections: {
                        ...prev_65.design.sections,
                        space_between_regular_entries: e_89.target.value
                      }
                    }
                  }))} className="theme-input w-full" placeholder="1.2em" />
                                            </div>
                                        </div>
                                    </details>

                                    {/* Locale Settings */}
                                    <details className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                                        <summary className={`text-sm font-semibold cursor-pointer ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>🌐 Locale</summary>
                                        <div className="mt-4 grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="theme-label">Language</label>
                                                <input type="text" value={resume.locale?.language || ''} onChange={e_90 => setResume(prev_66 => ({
                    ...prev_66,
                    locale: {
                      ...prev_66.locale,
                      language: e_90.target.value
                    }
                  }))} className="theme-input w-full" placeholder="en" />
                                            </div>
                                            <div>
                                                <label className="theme-label">Phone Format</label>
                                                <select value={resume.locale?.phone_number_format || 'national'} onChange={e_91 => setResume(prev_67 => ({
                    ...prev_67,
                    locale: {
                      ...prev_67.locale,
                      phone_number_format: e_91.target.value as any
                    }
                  }))} className="theme-input w-full">
                                                    <option value="national" className={isDarkMode ? 'bg-slate-800' : 'bg-white'}>National</option>
                                                    <option value="international" className={isDarkMode ? 'bg-slate-800' : 'bg-white'}>International</option>
                                                    <option value="e164" className={isDarkMode ? 'bg-slate-800' : 'bg-white'}>E.164</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="theme-label">"Present" Text</label>
                                                <input type="text" value={resume.locale?.present || ''} onChange={e_92 => setResume(prev_68 => ({
                    ...prev_68,
                    locale: {
                      ...prev_68.locale,
                      present: e_92.target.value
                    }
                  }))} className="theme-input w-full" placeholder="present" />
                                            </div>
                                            <div>
                                                <label className="theme-label">"To" Separator</label>
                                                <input type="text" value={resume.locale?.to || ''} onChange={e_93 => setResume(prev_69 => ({
                    ...prev_69,
                    locale: {
                      ...prev_69.locale,
                      to: e_93.target.value
                    }
                  }))} className="theme-input w-full" placeholder="–" />
                                            </div>
                                            <div className="col-span-2">
                                                <label className="theme-label">Date Template</label>
                                                <input type="text" value={resume.locale?.date_template || ''} onChange={e_94 => setResume(prev_70 => ({
                    ...prev_70,
                    locale: {
                      ...prev_70.locale,
                      date_template: e_94.target.value
                    }
                  }))} className="theme-input w-full" placeholder="MONTH_ABBREVIATION YEAR" />
                                            </div>
                                            <div className="col-span-2">
                                                <label className="theme-label">Last Updated Template</label>
                                                <input type="text" value={resume.locale?.last_updated_date_template || ''} onChange={e_95 => setResume(prev_71 => ({
                    ...prev_71,
                    locale: {
                      ...prev_71.locale,
                      last_updated_date_template: e_95.target.value
                    }
                  }))} className="theme-input w-full" placeholder="Last updated in TODAY" />
                                            </div>
                                        </div>
                                    </details>

                                    {/* Custom Sections Management */}
                                    <details className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'}`} open>
                                        <summary className={`text-sm font-semibold cursor-pointer ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>📑 Custom Sections</summary>
                                        <p className={`text-xs mt-2 mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Create custom sections like "Certifications", "Publications", "Languages", etc.</p>

                                        {/* Existing custom sections */}
                                        {Object.entries(resume.cv._customSectionTypes || {}).map(([sectionName_0, entryType]) => <div key={sectionName_0} className={`flex items-center justify-between p-3 mb-2 rounded-lg ${isDarkMode ? 'bg-white/5' : 'bg-white border border-slate-200'}`}>
                                                <div>
                                                    <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>{sectionName_0}</span>
                                                    <span className={`ml-2 text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                                        ({ENTRY_TYPES.find(t_0 => t_0.id === entryType)?.name || entryType})
                                                    </span>
                                                </div>
                                                <button onClick={() => {
                  setResume(prev_72 => {
                    const newSections = {
                      ...prev_72.cv.sections
                    };
                    delete newSections[sectionName_0];
                    const newTypes = {
                      ...prev_72.cv._customSectionTypes
                    };
                    delete newTypes[sectionName_0];
                    return {
                      ...prev_72,
                      cv: {
                        ...prev_72.cv,
                        sections: newSections,
                        _customSectionTypes: newTypes
                      }
                    };
                  });
                }} className="text-red-400 hover:text-red-300 text-sm">
                                                    Remove
                                                </button>
                                            </div>)}

                                        {/* Add new section form */}
                                        <div className="flex gap-2 mt-3">
                                            <input type="text" id="new-section-name" className="theme-input flex-1" placeholder="Section name (e.g., Certifications)" />
                                            <select id="new-section-type" className={`px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-white border border-slate-200'}`} defaultValue="text">
                                                {ENTRY_TYPES.map(type => <option key={type.id} value={type.id} className={isDarkMode ? 'bg-slate-800' : 'bg-white'}>
                                                        {type.name}
                                                    </option>)}
                                            </select>
                                            <button onClick={() => {
                  const nameInput = document.getElementById('new-section-name') as HTMLInputElement;
                  const typeSelect = document.getElementById('new-section-type') as HTMLSelectElement;
                  const sectionName_1 = nameInput?.value?.trim();
                  const entryType_0 = typeSelect?.value as EntryTypeId;
                  if (sectionName_1 && !['Summary', 'Experience', 'Education', 'Skills'].includes(sectionName_1)) {
                    setResume(prev_73 => ({
                      ...prev_73,
                      cv: {
                        ...prev_73.cv,
                        sections: {
                          ...prev_73.cv.sections,
                          [sectionName_1]: []
                        },
                        _customSectionTypes: {
                          ...prev_73.cv._customSectionTypes,
                          [sectionName_1]: entryType_0
                        }
                      }
                    }));
                    nameInput.value = '';
                  }
                }} className="px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/50 rounded-lg text-sm font-medium transition-colors">
                                                + Add
                                            </button>
                                        </div>
                                    </details>
                                </div>}

                            {/* Custom Section Editing - Dynamic based on active section */}
                            {resume.cv._customSectionTypes && Object.keys(resume.cv._customSectionTypes).includes(activeSection) && <div className="space-y-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="theme-section-title">📑 {activeSection}</h2>
                                        <button onClick={() => {
                const entryType_1 = resume.cv._customSectionTypes?.[activeSection] || 'text';
                const newEntry = entryType_1 === 'text' ? '' : createEmptyEntry(entryType_1);
                setResume(prev_74 => ({
                  ...prev_74,
                  cv: {
                    ...prev_74.cv,
                    sections: {
                      ...prev_74.cv.sections,
                      [activeSection]: [...(prev_74.cv.sections[activeSection] || []), newEntry]
                    }
                  }
                }));
              }} className="px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/50 rounded-lg text-sm transition-colors">
                                            + Add Item
                                        </button>
                                    </div>

                                    <p className={`text-xs mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                        Entry type: {ENTRY_TYPES.find(t_1 => t_1.id === resume.cv._customSectionTypes?.[activeSection])?.name || 'Text'}
                                    </p>

                                    {(resume.cv.sections[activeSection] || []).length === 0 && <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                            No items yet. Click "+ Add Item" to add content to this section.
                                        </p>}

                                    {(resume.cv.sections[activeSection] || []).map((item_0, index_5) => <div key={index_5} className={`flex items-start gap-2 p-3 rounded-lg ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-200'}`}>
                                            <span className={`text-xs mt-2.5 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{index_5 + 1}.</span>
                                            {typeof item_0 === 'string' ? <input type="text" value={item_0} onChange={e_96 => setResume(prev_75 => ({
                ...prev_75,
                cv: {
                  ...prev_75.cv,
                  sections: {
                    ...prev_75.cv.sections,
                    [activeSection]: (prev_75.cv.sections[activeSection] || []).map((itm, i_2) => i_2 === index_5 ? e_96.target.value : itm)
                  }
                }
              }))} className="theme-input flex-1" placeholder="Enter content..." /> : <div className="flex-1 space-y-2">
                                                    {/* For structured entries, show key-value pairs */}
                                                    {Object.entries(item_0 as Record<string, any>).filter(([key_2]) => key_2 !== 'id').map(([key_3, value_5]) => <div key={key_3} className="flex items-center gap-2">
                                                            <label className={`text-xs w-24 capitalize ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{key_3.replace('_', ' ')}</label>
                                                            <input type="text" value={Array.isArray(value_5) ? value_5.join(', ') : String(value_5 || '')} onChange={e_97 => {
                    const newValue = Array.isArray(value_5) ? e_97.target.value.split(',').map(s_2 => s_2.trim()) : e_97.target.value;
                    setResume(prev_76 => {
                      const currentSection = prev_76.cv.sections[activeSection] || [];
                      const updatedSection = currentSection.map((itm_0, i_3) => i_3 === index_5 ? {
                        ...(itm_0 as Record<string, any>),
                        [key_3]: newValue
                      } : itm_0);
                      return {
                        ...prev_76,
                        cv: {
                          ...prev_76.cv,
                          sections: {
                            ...prev_76.cv.sections,
                            [activeSection]: updatedSection as AnyEntry[]
                          }
                        }
                      };
                    });
                  }} className="theme-input flex-1 text-sm" placeholder={`Enter ${key_3}...`} />
                                                        </div>)}
                                                </div>}
                                            <button onClick={() => setResume(prev_77 => ({
                ...prev_77,
                cv: {
                  ...prev_77.cv,
                  sections: {
                    ...prev_77.cv.sections,
                    [activeSection]: (prev_77.cv.sections[activeSection] || []).filter((__0, i_4) => i_4 !== index_5)
                  }
                }
              }))} className="text-red-400 hover:text-red-300 p-1.5 rounded hover:bg-red-500/10 transition-colors" title="Remove">
                                                ✕
                                            </button>
                                        </div>)}
                                </div>}

                        </div>}
                </div>

                {/* Right Panel - Preview, Score & AI Coach */}
                <div className={`flex flex-col w-full md:w-1/2 min-h-[60vh] md:min-h-0 ${isDarkMode ? '' : 'bg-slate-50'}`}>
                    {/* Score Bar */}
                    <div className={`p-4 border-b transition-colors ${isDarkMode ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200'}`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>ATS Score:</span>
                                    {scoreLoading ? <span className="text-slate-400">...</span> : score ? <span className={`text-2xl font-bold ${getScoreColor(score.total_score)}`}>
                                            {score.total_score}
                                        </span> : <span className="text-slate-500">--</span>}
                                    <span className="text-slate-500 text-sm">/100</span>
                                </div>

                                {score?.grade && <span className={`px-2 py-0.5 rounded text-xs font-bold ${score.grade === 'A' ? 'bg-green-500/20 text-green-600' : score.grade === 'B' ? 'bg-yellow-500/20 text-yellow-600' : score.grade === 'C' ? 'bg-orange-500/20 text-orange-600' : 'bg-red-500/20 text-red-600'}`}>
                                        Grade {score.grade}
                                    </span>}
                            </div>

                            <div className="flex items-center gap-2">
                                <button onClick={() => setShowJdInput(!showJdInput)} className="text-xs text-purple-500 hover:text-purple-600 font-medium">
                                    {showJdInput ? 'Hide' : 'Add'} Job Description
                                </button>

                                {/* Manual Check Score Button */}
                                <button onClick={() => {
                updateScore();
                setHasChanges(false);
              }} disabled={scoreLoading || !resume.cv.name} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${hasChanges && !scoreLoading ? 'bg-purple-500 hover:bg-purple-600 text-white animate-pulse' : isDarkMode ? 'bg-white/10 hover:bg-white/20 text-slate-300' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'} disabled:opacity-50`}>
                                    {scoreLoading ? <><span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin"></span> Checking...</> : <><span>🔍</span> Check Score</>}
                                </button>
                            </div>
                        </div>

                        {/* Job Description Input */}
                        {showJdInput && <div className="mt-3">
                                <textarea value={jobDescription} onChange={e_98 => setJobDescription(e_98.target.value)} className="theme-input text-xs h-20 resize-none" placeholder="Paste job description here for accurate keyword matching..." />
                            </div>}

                        {/* Score Breakdown - Collapsible */}
                        {score && <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                                <div className={`p-2 rounded-lg text-center ${isDarkMode ? 'bg-white/5' : 'bg-slate-100'}`}>
                                    <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Contact</div>
                                    <div className={`text-sm font-bold ${getScoreColor(score.breakdown?.contact_info?.score / score.breakdown?.contact_info?.max_score * 100)}`}>
                                        {score.breakdown?.contact_info?.score || 0}/{score.breakdown?.contact_info?.max_score || 15}
                                    </div>
                                </div>
                                <div className={`p-2 rounded-lg text-center ${isDarkMode ? 'bg-white/5' : 'bg-slate-100'}`}>
                                    <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Sections</div>
                                    <div className={`text-sm font-bold ${getScoreColor(score.breakdown?.sections?.score / score.breakdown?.sections?.max_score * 100)}`}>
                                        {score.breakdown?.sections?.score || 0}/{score.breakdown?.sections?.max_score || 30}
                                    </div>
                                </div>
                                <div className={`p-2 rounded-lg text-center ${isDarkMode ? 'bg-white/5' : 'bg-slate-100'}`}>
                                    <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Keywords</div>
                                    <div className={`text-sm font-bold ${getScoreColor(score.breakdown?.keywords?.score / score.breakdown?.keywords?.max_score * 100)}`}>
                                        {score.breakdown?.keywords?.score || 0}/{score.breakdown?.keywords?.max_score || 40}
                                    </div>
                                </div>
                                <div className={`p-2 rounded-lg text-center ${isDarkMode ? 'bg-white/5' : 'bg-slate-100'}`}>
                                    <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Format</div>
                                    <div className={`text-sm font-bold ${getScoreColor(score.breakdown?.formatting?.score / score.breakdown?.formatting?.max_score * 100)}`}>
                                        {score.breakdown?.formatting?.score || 0}/{score.breakdown?.formatting?.max_score || 15}
                                    </div>
                                </div>
                            </div>}

                        {/* Missing Keywords - Click to Add */}
                        {score?.breakdown?.keywords?.missing_keywords?.length > 0 && <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                                <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>❌ Missing Keywords (click to add to skills):</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {score.breakdown.keywords.missing_keywords.slice(0, 12).map((kw_0: string, i_5: number) => <button key={i_5} onClick={() => addKeywordToSkills(kw_0)} className={`px-2 py-1 bg-red-500/20 hover:bg-purple-500/30 text-xs rounded-full transition-colors cursor-pointer ${isDarkMode ? 'text-red-200 hover:text-purple-200' : 'text-red-800 hover:text-purple-800'}`}>
                                            + {kw_0}
                                        </button>)}
                                </div>
                            </div>}
                    </div>

                    {/* Main Content Area - Split between Preview and AI Coach */}
                    <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
                        {/* Preview Area */}
                        <div className={`${showCoach ? 'md:w-1/2 md:border-r' : 'w-full'} ${showCoach && isDarkMode ? 'border-white/10' : showCoach ? 'border-slate-200' : ''} overflow-auto p-2 md:p-4 transition-all ${isDarkMode ? 'bg-slate-950/50' : 'bg-slate-100'} min-h-[200px] md:min-h-0`}>
                            {previewLoading && <div className="flex items-center justify-center h-full">
                                    <div className="text-center">
                                        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                                        <p className="text-slate-400 text-sm">Generating preview...</p>
                                    </div>
                                </div>}

                            {!previewLoading && previewError && <div className="flex items-center justify-center h-full p-4">
                                    <div className={`p-6 rounded-lg border max-w-lg w-full ${isDarkMode ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'}`}>
                                        <div className="flex items-center gap-3 mb-3 text-red-500">
                                            <span className="text-xl">⚠️</span>
                                            <span className="font-semibold">Unable to Generate Preview</span>
                                        </div>
                                        <div className={`text-xs font-mono whitespace-pre-wrap overflow-auto max-h-[300px] p-4 rounded ${isDarkMode ? 'bg-black/30 text-red-300' : 'bg-white text-red-600 border border-red-100'}`}>
                                            {previewError}
                                        </div>
                                        <p className="mt-4 text-sm text-slate-500">Check your resume details for invalid characters or missing required fields.</p>
                                    </div>
                                </div>}

                            {!previewLoading && !previewError && previewUrl && <div className="w-full h-full">
                                    <iframe src={previewUrl} title="Resume Preview" className="w-full h-full min-h-[500px] shadow-2xl rounded-lg border-none" />
                                </div>}

                            {!previewLoading && !previewUrl && <div className="flex items-center justify-center h-full">
                                    <div className="text-center text-slate-500">
                                        <p className="text-4xl mb-4">📄</p>
                                        <p>Start editing to see your resume preview</p>
                                        <p className="text-sm mt-2">Add your name to begin</p>
                                    </div>
                                </div>}
                        </div>

                        {/* AI Coach Panel - Modal on mobile, Sidebar on Desktop */}
                        {showCoach && <div className={`fixed inset-0 z-[60] md:static md:z-auto w-full md:w-1/2 flex flex-col md:min-h-0 ${isDarkMode ? 'bg-slate-900' : 'bg-white'} md:bg-transparent`}>
                                {/* Coach Header */}
                                <div className={`p-3 border-b flex items-center justify-between ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
                                    <div className="flex items-center gap-2">
                                        <span className="text-lg">🤖</span>
                                        <span className="font-semibold text-sm">AI Resume Coach</span>
                                    </div>
                                    <button onClick={() => setShowCoach(false)} className={`w-6 h-6 rounded flex items-center justify-center text-xs ${isDarkMode ? 'bg-white/10 hover:bg-white/20' : 'bg-slate-100 hover:bg-slate-200 text-slate-500'}`}>
                                        ✕
                                    </button>
                                </div>

                                {/* Coach Messages */}
                                <div className="flex-1 overflow-y-auto p-3 space-y-3">
                                    {coachMessages.map((msg, idx) => <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                            <div className={`max-w-[85%] p-3 rounded-xl text-sm ${msg.role === 'user' ? 'bg-purple-500/30 border border-purple-500/30' : isDarkMode ? 'bg-white/10 border border-white/10' : 'bg-slate-100 border border-slate-200 text-slate-700'}`}>
                                                <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                                            </div>
                                        </div>)}
                                    {coachLoading && <div className="flex justify-start">
                                            <div className={`border p-3 rounded-xl ${isDarkMode ? 'bg-white/10 border-white/10' : 'bg-slate-100 border-slate-200'}`}>
                                                <div className="flex gap-1">
                                                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{
                      animationDelay: '0ms'
                    }}></div>
                                                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{
                      animationDelay: '150ms'
                    }}></div>
                                                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{
                      animationDelay: '300ms'
                    }}></div>
                                                </div>
                                            </div>
                                        </div>}
                                </div>

                                {/* Quick Suggestions */}
                                {coachMessages.length <= 2 && <div className="px-3 pb-2">
                                        <p className="text-xs text-slate-400 mb-1">Try asking:</p>
                                        <div className="flex flex-wrap gap-1">
                                            {["Improve my experience bullets", "Suggest keywords for this role", "Write a better summary"].map((q, i_6) => <button key={i_6} onClick={() => setCoachInput(q)} className={`px-2 py-1 rounded text-xs border ${isDarkMode ? 'bg-white/5 hover:bg-white/10 border-white/10' : 'bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-600'}`}>
                                                    {q}
                                                </button>)}
                                        </div>
                                    </div>}

                                {/* Coach Input */}
                                <div className={`p-3 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
                                    <div className="flex gap-2">
                                        <input type="text" value={coachInput} onChange={e_99 => setCoachInput(e_99.target.value)} onKeyPress={e_100 => e_100.key === 'Enter' && sendCoachMessage()} placeholder="Ask about your resume..." className="theme-input flex-1" disabled={coachLoading} />
                                        <button onClick={sendCoachMessage} disabled={coachLoading || !coachInput.trim()} className="px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:bg-slate-600 rounded-lg text-sm font-medium transition-colors">
                                            {coachLoading ? '...' : '→'}
                                        </button>
                                    </div>
                                </div>
                            </div>}
                    </div>
                </div>
            </div>
            {/* Master Panel */}
            <MasterPanel isOpen={showMasterPanel} onClose={() => setShowMasterPanel(false)} position={masterPanelPosition} selectedText={selectedMasterText} resumeDesign={resume.design || {}} isDarkMode={isDarkMode} onApplyFormat={(format, url_4) => {
      if (!activeInputRef) return;
      const input_2 = activeInputRef;
      const start = input_2.selectionStart;
      const end = input_2.selectionEnd;
      if (start === null || end === null) return;
      const text_2 = input_2.value;
      const before = text_2.substring(0, start);
      const selected = text_2.substring(start, end);
      const after = text_2.substring(end);
      let newText = '';
      if (format === 'bold') {
        newText = `${before}**${selected}**${after}`;
      } else if (format === 'italic') {
        newText = `${before}*${selected}*${after}`;
      } else if (format === 'link') {
        newText = `${before}[${selected}](${url_4 || ''})${after}`;
      }
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;
      const nativeTextAreaValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, "value")?.set;
      const setter = input_2 instanceof HTMLTextAreaElement ? nativeTextAreaValueSetter : nativeInputValueSetter;
      if (setter) {
        setter.call(input_2, newText);
        input_2.dispatchEvent(new Event('input', {
          bubbles: true
        }));
      }
    }} onUpdateDesign={(path, value_6) => {
      setResume(prev_78 => {
        const newResume = {
          ...prev_78
        };
        if (!newResume.design) newResume.design = {} as any;
        const parts = path.split('.');
        let current: any = newResume.design;
        for (let i_7 = 0; i_7 < parts.length - 1; i_7++) {
          if (!current[parts[i_7]]) current[parts[i_7]] = {};
          current = current[parts[i_7]];
        }
        current[parts[parts.length - 1]] = value_6;
        return newResume;
      });
    }} />

            {/* Daily Limit Reached Dialog */}
            <LimitReachedDialog isOpen={showLimitDialog} onClose={() => setShowLimitDialog(false)} />
        </div>;
}